import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AppContext";
import {
  listProducts, saveProduct, deleteProduct, uploadProductImage, subscribeProducts,
  listOrders, updateOrder, deleteOrder, subscribeOrders,
  listExpenses, saveExpense, deleteExpense, subscribeExpenses,
  listInvestments, saveInvestment, deleteInvestment, subscribeInvestments,
  listWithdrawals, saveWithdrawal, deleteWithdrawal, subscribeWithdrawals,
  getOpeningBalances, saveOpeningBalances, subscribeOpeningBalances, DEFAULT_OPENING_BALANCES,
  listLosses, saveLoss, deleteLoss, subscribeLosses,
  listMarketplaceOrders, saveMarketplaceOrder, deleteMarketplaceOrder, subscribeMarketplaceOrders,
  listMasterSkus, saveMasterSku, subscribeMasterSkus,
  authenticateGoogleDrive, hasDriveAccessToken, getDriveAccessToken, getDriveUserEmail,
  disconnectGoogleDrive, getDriveFolderConfig, setDriveFolderConfig,
} from "../lib/dataStore";
import { toast } from "sonner";
import {
  Plus, Edit3, Trash2, Package, ShoppingBag, X, Upload, Link as LinkIcon,
   BarChart3, IndianRupee, Boxes, TrendingUp, ExternalLink, Copy, Printer,
  DollarSign, Receipt, ArrowDownRight, ArrowUpRight, PieChart, Calculator,
  Layers, Check, Filter, Search, Wallet, UserCheck, ArrowDownLeft, ShieldAlert, History, Truck, Building2, Store,
  RefreshCw, CheckCircle2, FileSpreadsheet, Folder, HardDrive, Barcode, Tag
} from "lucide-react";
import { driveUrlToImage, normaliseImageUrl, handleImageError } from "../lib/driveImage";
import { isSoldOut, primaryImage, parseComboQty } from "../lib/productHelpers";
import { orderGrandTotal, orderSubtotal, orderDiscount, orderShipping, invoiceShipping, orderTaxTotal, orderPurchaseCost, orderExtraCharge, orderCodCharge, orderAdvanceAmount, orderBalanceDue, calculateSingleOrderProfit, recalculateOrderFinancials, CARRIER_OPTIONS, getCarrierType, getCarrierTrackingUrl, STATUS_CONFIG, isWestBengalState } from "../lib/orderHelpers";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import OfflineOrderModal from "../components/OfflineOrderModal";
import MarketplaceOrderModal from "../components/MarketplaceOrderModal";
import GstSalesPanel from "../components/GstSalesPanel";
import MasterSkuPanel from "../components/MasterSkuPanel";
import ProductLabelModal from "../components/ProductLabelModal";
import { EditOrderItemsModal, EditCustomerModal, DeleteOrderConfirmModal } from "../components/OrderEditModals";

const CATS = ["Leather Bags", "Cotton Bags", "Accessories"];
const STATUSES = ["New", "Confirmed", "Shipped", "Delivered", "Cancelled"];
const RANGES = { "7":  "Last 7 days", "30": "Last 30 days", "90": "Last 90 days", "365": "This year", "all": "All time" };

const emptyProduct = () => ({
  sku: "", name: "", description: "", bagType: "", size: "", weight: "", colour: "", material: "", price: "", mrp: "", shippingCharge: "0", gstRate: "0", category: "Leather Bags",
  productCost: "", packagingCost: "80", extraCharge: "0", videoUrl: "",
  variants: [{ tag: "Default", stock: "", comboQty: "1", price: "", mrp: "", productCost: "", packagingCost: "", extraCharge: "", images: [] }],
});

export default function Admin() {
  const { user, isAdmin, loading } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [openingBalances, setOpeningBalances] = useState(DEFAULT_OPENING_BALANCES);
  const [losses, setLosses] = useState([]);
  const [marketplaceOrders, setMarketplaceOrders] = useState([]);
  const [masterSkus, setMasterSkus] = useState([]);
  const [editing, setEditing] = useState(null);

  // Modal opener from MasterSkuPanel to create an invoice directly with that SKU
  const [skuInvoiceModalItem, setSkuInvoiceModalItem] = useState(null);
  const [showSkuInvoiceModal, setShowSkuInvoiceModal] = useState(false);

  // Product Label & Barcode Generator state
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelModalItem, setLabelModalItem] = useState(null);

  const refresh = async (force = false) => {
    setProducts(await listProducts(force));
    setOrders(await listOrders(force));
    setExpenses(await listExpenses(force));
    setInvestments(await listInvestments(force));
    setWithdrawals(await listWithdrawals(force));
    setOpeningBalances(await getOpeningBalances(force));
    setLosses(await listLosses(force));
    setMarketplaceOrders(await listMarketplaceOrders(force));
    setMasterSkus(await listMasterSkus(force));
  };

  useEffect(() => {
    if (!isAdmin) return;
    const unsubP = subscribeProducts(setProducts);
    const unsubO = subscribeOrders(setOrders);
    const unsubE = subscribeExpenses(setExpenses);
    const unsubI = subscribeInvestments(setInvestments);
    const unsubW = subscribeWithdrawals(setWithdrawals);
    const unsubB = subscribeOpeningBalances(setOpeningBalances);
    const unsubL = subscribeLosses(setLosses);
    const unsubM = subscribeMarketplaceOrders(setMarketplaceOrders);
    const unsubMS = subscribeMasterSkus(setMasterSkus);

    return () => {
      unsubP && unsubP();
      unsubO && unsubO();
      unsubE && unsubE();
      unsubI && unsubI();
      unsubW && unsubW();
      unsubB && unsubB();
      unsubL && unsubL();
      unsubM && unsubM();
      unsubMS && unsubMS();
    };
  }, [isAdmin]);

  if (loading) return <div className="p-16 text-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return (
    <div className="max-w-3xl mx-auto p-16 text-center">
      <h1 className="font-serif text-4xl mb-3">Not authorised</h1>
      <p className="text-muted-foreground">This area is reserved for the Lineacraft admin.</p>
    </div>
  );

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button data-testid={`tab-${id}`} onClick={() => setTab(id)}
      className={`px-4 py-2 rounded-full text-sm flex items-center gap-2 ${tab === id ? "bg-background shadow-sm font-medium text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline text-terracotta mb-1">Lineacraft Studio Admin</div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold">Studio Operations &amp; Accounting</h1>
        </div>
        <div className="flex flex-wrap gap-1 bg-muted p-1 rounded-full border border-border">
          <TabBtn id="dashboard" icon={BarChart3} label="Dashboard" />
          <TabBtn id="finances" icon={IndianRupee} label="Costing &amp; Accounting" />
          <TabBtn id="masterSkus" icon={Boxes} label="Master SKU List" />
          <TabBtn id="products" icon={Package} label="Products" />
          <TabBtn id="orders" icon={ShoppingBag} label="Orders" />
          <TabBtn id="labels" icon={Barcode} label="Product Labels" />
          <TabBtn id="gst" icon={FileSpreadsheet} label="GST Reports" />
        </div>
      </div>

      {tab === "dashboard" && (
        <Dashboard
          orders={orders}
          products={products}
          expenses={expenses}
          investments={investments}
          withdrawals={withdrawals}
          openingBalances={openingBalances}
          losses={losses}
          marketplaceOrders={marketplaceOrders}
          refresh={refresh}
        />
      )}

      {tab === "finances" && (
        <FinancesPanel
          orders={orders}
          products={products}
          expenses={expenses}
          investments={investments}
          withdrawals={withdrawals}
          openingBalances={openingBalances}
          losses={losses}
          marketplaceOrders={marketplaceOrders}
          refresh={refresh}
        />
      )}

      {tab === "masterSkus" && (
        <MasterSkuPanel
          masterSkus={masterSkus}
          products={products}
          refresh={refresh}
          onOpenInvoiceWithSku={(skuItem) => {
            setSkuInvoiceModalItem(skuItem);
            setShowSkuInvoiceModal(true);
          }}
        />
      )}

      {tab === "products" && (
        <ProductsPanel
          products={products}
          onNew={() => setEditing(emptyProduct())}
          onEdit={(p) => setEditing(JSON.parse(JSON.stringify(p)))}
          onDelete={async (id) => { await deleteProduct(id); toast.success("Deleted"); refresh(); }}
          onGenerateLabel={(p) => {
            setLabelModalItem(p);
            setShowLabelModal(true);
          }}
        />
      )}

      {tab === "orders" && (
        <OrdersPanel
          orders={orders}
          products={products}
          marketplaceOrders={marketplaceOrders}
          masterSkus={masterSkus}
          refresh={refresh}
        />
      )}

      {tab === "gst" && <GstSalesPanel orders={orders} products={products} refresh={refresh} />}

      {tab === "sheets" && <SheetMigrationPanel products={products} orders={orders} expenses={expenses} refresh={refresh} />}

      {/* Offline Order / Invoice Modal triggered with preselected SKU */}
      {showSkuInvoiceModal && (
        <OfflineOrderModal
          products={products}
          masterSkus={masterSkus}
          initialSkuItem={skuInvoiceModalItem}
          onClose={() => {
            setShowSkuInvoiceModal(false);
            setSkuInvoiceModalItem(null);
          }}
          onSaved={() => {
            setShowSkuInvoiceModal(false);
            setSkuInvoiceModalItem(null);
            refresh();
          }}
        />
      )}

      {editing && (
        <ProductEditor
          initial={editing}
          masterSkus={masterSkus}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
          onGenerateLabel={(p) => {
            setLabelModalItem(p);
            setShowLabelModal(true);
          }}
        />
      )}

      {/* Product Label & Barcode Generator Modal */}
      {(showLabelModal || tab === "labels") && (
        <ProductLabelModal
          isOpen={true}
          onClose={() => {
            setShowLabelModal(false);
            if (tab === "labels") setTab("products");
          }}
          initialItem={labelModalItem}
          masterSkus={masterSkus}
          products={products}
        />
      )}
    </div>
  );
}

// ============================================================================
// DASHBOARD
// ============================================================================
function Dashboard({
  orders = [],
  products = [],
  expenses = [],
  investments = [],
  withdrawals = [],
  openingBalances = {},
  losses = [],
  marketplaceOrders = [],
  refresh,
}) {
  const [range, setRange] = useState("30");
  const [showCashAudit, setShowCashAudit] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const openRev = Number(openingBalances.openingRevenue || 0);
  const openOrdersCount = Number(openingBalances.openingOrdersCount || 0);
  const openQtySold = Number(openingBalances.openingQuantitySold || 0);
  const openCOGS = Number(openingBalances.openingCOGS || 0);
  const openShip = Number(openingBalances.openingShipping || 0);
  const openExp = Number(openingBalances.openingExpenses || 0);
  const openLosses = Number(openingBalances.openingLosses || 0);
  const openProfit = Number(openingBalances.openingProfit || 0);
  const openCap = Number(openingBalances.openingCapital || 0);
  const openCapShuvasis = Number(openingBalances.openingCapitalShuvasis || 0);
  const openCapSourav = Number(openingBalances.openingCapitalSourav || 0);
  const openDraws = Number(openingBalances.openingWithdrawals || 0);
  const openDrawsShuvasis = Number(openingBalances.openingWithdrawalsShuvasis || 0);
  const openDrawsSourav = Number(openingBalances.openingWithdrawalsSourav || 0);
  const openCash = Number(openingBalances.openingCashOnHand || 0);

  const filteredOrders = useMemo(() => {
    if (range === "all") return orders;
    const days = Number(range);
    const cutoff = Date.now() - days * 86400_000;
    return orders.filter((o) => {
      const t = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : new Date(o.createdAt).getTime();
      return t >= cutoff;
    });
  }, [orders, range]);

  const validOrders = useMemo(() => orders.filter((o) => o.status !== "Cancelled"), [orders]);
  const cancelledOrders = useMemo(() => orders.filter((o) => o.status === "Cancelled"), [orders]);

  // Marketplace Totals
  const totalMarketplaceGross = useMemo(() => marketplaceOrders.reduce((s, m) => s + Number(m.grossSales || 0), 0), [marketplaceOrders]);
  const totalMarketplacePayout = useMemo(() => marketplaceOrders.reduce((s, m) => s + Number(m.netPayout || 0), 0), [marketplaceOrders]);
  const totalMarketplaceUnits = useMemo(() => marketplaceOrders.reduce((s, m) => s + Number(m.unitsSold || 0), 0), [marketplaceOrders]);
  const totalMarketplaceProfit = useMemo(() => marketplaceOrders.reduce((s, m) => s + Number(m.netProfit || 0), 0), [marketplaceOrders]);

  // Overall Financial Calculations
  const directSalesRevenue = useMemo(() => validOrders.reduce((sum, o) => sum + orderGrandTotal(o, products), 0), [validOrders, products]);
  const grossSalesRevenue = openRev + directSalesRevenue + totalMarketplaceGross;
  const courierOutflow = useMemo(() => openShip + validOrders.reduce((sum, o) => sum + orderShipping(o, products), 0), [validOrders, openShip, products]);
  
  // Completed Orders & Quantity Sold
  const liveQtySold = useMemo(() => {
    return validOrders.reduce((sum, o) => {
      return sum + (o.items || []).reduce((iq, it) => iq + Number(it.quantity || 1), 0);
    }, 0);
  }, [validOrders]);
  const totalQuantitySold = openQtySold + liveQtySold + totalMarketplaceUnits;
  const totalCompletedOrders = openOrdersCount + validOrders.length + marketplaceOrders.length;

  // Total Recorded Losses
  const liveLosses = useMemo(() => losses.reduce((s, l) => s + Number(l.amount || 0), 0), [losses]);
  const totalLosses = openLosses + liveLosses;

  // Live Expenses categorization to prevent double counting
  const cogsCategories = ["Raw Materials", "Hardware & Accessories", "Artisan Wages", "Packaging"];
  const cogsExpenses = useMemo(() => {
    return expenses
      .filter((e) => cogsCategories.includes(e.category))
      .reduce((s, e) => s + Number(e.amount || 0), 0);
  }, [expenses]);

  const operationalExpenses = useMemo(() => {
    return expenses
      .filter((e) => !cogsCategories.includes(e.category))
      .reduce((s, e) => s + Number(e.amount || 0), 0);
  }, [expenses]);

  const liveExpenses = useMemo(() => expenses.reduce((s, e) => s + Number(e.amount || 0), 0), [expenses]);

  // COGS / Purchase Expense = openCOGS + live raw materials/production expenses
  const totalCOGS = useMemo(() => openCOGS + cogsExpenses, [openCOGS, cogsExpenses]);

  // Total Operational Expenses = openExp + other operational expenses
  const totalOperationalExpenses = useMemo(() => openExp + operationalExpenses, [openExp, operationalExpenses]);

  // Total all expenses paid (materials + operations) without duplication
  const totalAllExpenses = useMemo(() => openCOGS + openExp + liveExpenses, [openCOGS, openExp, liveExpenses]);

  const totalInvestments = useMemo(() => openCap + investments.reduce((s, i) => s + Number(i.amount || 0), 0), [investments, openCap]);
  const totalWithdrawals = useMemo(() => openDraws + withdrawals.reduce((s, w) => s + Number(w.amount || 0), 0), [withdrawals, openDraws]);

  // Total Net Capital Injected
  const totalCapitalInjected = totalInvestments - totalWithdrawals;

  // Total Liquid Cash Inflow from Sales (Direct store collected + baseline sales + marketplace bank payouts)
  const totalSalesCashInflow = openRev + directSalesRevenue + totalMarketplacePayout;

  // Exact In-Hand Liquid Cash = Starting Cash + Net Capital Injected + Actual Sales Cash Inflow − Total Expenses Outflow − Courier Shipping − Losses
  const inHandCash = openCash + totalCapitalInjected + totalSalesCashInflow - totalAllExpenses - courierOutflow - totalLosses;

  // Live Order Profit Calculation (Direct Website/Offline + Marketplace Settlements)
  const liveDirectOrderProfit = useMemo(() => {
    return validOrders.reduce((sum, o) => {
      return sum + calculateSingleOrderProfit(o, products);
    }, 0);
  }, [validOrders, products]);

  // Net Business Profit = openProfit + liveDirectOrderProfit + totalMarketplaceProfit - liveLosses
  const netProfit = openProfit + liveDirectOrderProfit + totalMarketplaceProfit - liveLosses;
  const netMargin = grossSalesRevenue > 0 ? ((netProfit / grossSalesRevenue) * 100).toFixed(1) : "0.0";

  // Detailed Multi-Timeframe Analytics Map
  const timeframeStatsMap = useMemo(() => {
    const periodKeys = ["7", "30", "90", "365", "all"];
    const result = {};

    for (const key of periodKeys) {
      const isAll = key === "all";
      const days = isAll ? null : Number(key);
      const cutoff = isAll ? 0 : Date.now() - days * 86400000;

      const pOrders = validOrders.filter((o) => {
        if (isAll) return true;
        const t = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : new Date(o.createdAt).getTime();
        return t >= cutoff;
      });

      const pExpenses = expenses.filter((e) => {
        if (isAll) return true;
        const t = e.createdAt?.seconds ? e.createdAt.seconds * 1000 : new Date(e.createdAt).getTime();
        return t >= cutoff;
      });

      const pLosses = losses.filter((l) => {
        if (isAll) return true;
        const t = l.createdAt?.seconds ? l.createdAt.seconds * 1000 : new Date(l.createdAt).getTime();
        return t >= cutoff;
      });

      const ordersCount = (isAll ? openOrdersCount : 0) + pOrders.length;

      const bagsSold = (isAll ? openQtySold : 0) + pOrders.reduce((sum, o) => {
        return sum + (o.items || []).reduce((iq, it) => iq + Number(it.quantity || 1), 0);
      }, 0);

      const sales = (isAll ? openRev : 0) + pOrders.reduce((sum, o) => sum + orderGrandTotal(o, products), 0);
      const shipping = (isAll ? openShip : 0) + pOrders.reduce((sum, o) => sum + orderShipping(o, products), 0);

      const cogs = (isAll ? openCOGS : 0) + pOrders.reduce((sum, o) => {
        if (o.purchaseCost != null && o.purchaseCost !== "") return sum + Number(o.purchaseCost);
        return sum + (o.items || []).reduce((itemSum, it) => {
          const prod = products.find((p) => p.id === it.id);
          const cost = prod?.productCost != null && prod.productCost !== "" ? Number(prod.productCost) : (prod?.costPrice != null ? Number(prod.costPrice) : Math.round(Number(it.price || 0) * 0.45));
          const pack = prod?.packagingCost != null && prod.packagingCost !== "" ? Number(prod.packagingCost) : 80;
          return itemSum + (cost + pack) * Number(it.quantity || 1);
        }, 0);
      }, 0);

      const opExpenses = (isAll ? openExp : 0) + pExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);
      const lossesVal = (isAll ? openLosses : 0) + pLosses.reduce((s, l) => s + Number(l.amount || 0), 0);

      const profitVal = sales - shipping - cogs;
      const marginVal = sales > 0 ? ((profitVal / sales) * 100).toFixed(1) : "0.0";

      result[key] = {
        key,
        label: RANGES[key],
        ordersCount,
        bagsSold,
        sales,
        shipping,
        cogs,
        opExpenses,
        lossesVal,
        profit: profitVal,
        margin: marginVal,
      };
    }
    return result;
  }, [validOrders, expenses, losses, products, openOrdersCount, openQtySold, openRev, openShip, openCOGS, openExp, openLosses, openProfit]);

  const currentStats = timeframeStatsMap[range] || timeframeStatsMap["30"];

  // Partner Shares: Shuvasis & Sourav
  const shuvasisInvested = openCapShuvasis + investments.filter(i => (i.investedBy || "").toLowerCase() === "shuvasis").reduce((s, i) => s + Number(i.amount || 0), 0);
  const souravInvested = openCapSourav + investments.filter(i => (i.investedBy || "").toLowerCase() === "sourav").reduce((s, i) => s + Number(i.amount || 0), 0);
  const shuvasisWithdrawn = openDrawsShuvasis + withdrawals.filter(w => (w.withdrawnBy || "").toLowerCase() === "shuvasis").reduce((s, w) => s + Number(w.amount || 0), 0);
  const souravWithdrawn = openDrawsSourav + withdrawals.filter(w => (w.withdrawnBy || "").toLowerCase() === "sourav").reduce((s, w) => s + Number(w.amount || 0), 0);

  const shuvasisNetCapital = shuvasisInvested - shuvasisWithdrawn;
  const souravNetCapital = souravInvested - souravWithdrawn;

  const halfProfit = Math.round(netProfit / 2);
  const shuvasisEquity = shuvasisNetCapital + halfProfit;
  const souravEquity = souravNetCapital + halfProfit;

  // Monthly Chart
  const monthly = useMemo(() => {
    const map = new Map();
    for (const o of validOrders) {
      const t = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : new Date(o.createdAt).getTime();
      const d = new Date(t);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
      const cur = map.get(key) || { key, label, revenue: 0, orders: 0 };
      cur.revenue += orderGrandTotal(o, products);
      cur.orders += 1;
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key)).slice(-6);
  }, [validOrders, products]);

  const soldOutCount = products.filter(isSoldOut).length;
  const lowStock = products.filter((p) => !isSoldOut(p) && Number(p.stock) <= 5).length;

  return (
    <div className="space-y-8 animate-fade-up">
      {/* Liquid Cash Hero Card & Key Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total In-Hand Cash Balance Card */}
        <div className="md:col-span-1 bg-gradient-to-br from-amber-900/90 via-amber-800 to-terracotta text-white rounded-2xl p-6 shadow-xl relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 transform translate-x-4 -translate-y-4 opacity-10 pointer-events-none">
            <Wallet className="w-40 h-40" />
          </div>
          <div>
            <div className="flex items-center justify-between text-amber-200 text-xs uppercase tracking-wider font-semibold mb-2">
              <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4" /> Total In-Hand Cash</span>
              <span className="bg-amber-700/60 px-2 py-0.5 rounded text-[10px] flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Real-time Liquid
              </span>
            </div>
            <div className="text-4xl font-price font-extrabold tracking-tight text-white mt-1">
              ₹{inHandCash.toLocaleString("en-IN")}
            </div>
            <p className="text-xs text-amber-100/80 mt-2 leading-relaxed">
              {openCash > 0 && <span>Opening Cash (<span className="font-price font-semibold">₹{openCash.toLocaleString()}</span>) + </span>}
              Net Capital (<span className="font-price font-semibold">₹{totalCapitalInjected.toLocaleString()}</span>) + Sales Inflow (<span className="font-price font-semibold">₹{totalSalesCashInflow.toLocaleString()}</span>) − Total Expenses (<span className="font-price font-semibold">₹{totalAllExpenses.toLocaleString()}</span>) − Shipping (<span className="font-price font-semibold">₹{courierOutflow.toLocaleString()}</span>) − Losses (<span className="font-price font-semibold">₹{totalLosses.toLocaleString()}</span>).
            </p>

            <button
              type="button"
              onClick={() => setShowCashAudit(true)}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/15 hover:bg-white/25 text-white text-[11px] font-medium transition cursor-pointer border border-white/20"
            >
              <Calculator className="w-3.5 h-3.5" />
              View In-Hand Cash Audit &amp; Cloud Data
            </button>
          </div>
          <div className="pt-4 mt-4 border-t border-amber-700/50 flex items-center justify-between text-xs text-amber-100">
            <span>Capital Pool: <strong className="font-price">₹{totalInvestments.toLocaleString()}</strong></span>
            <span>Partner Draws: <strong className="font-price">₹{totalWithdrawals.toLocaleString()}</strong></span>
          </div>
        </div>

        {/* Net Business Profit Card */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              <span className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-emerald-600" /> Net Business Profit</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${netProfit >= 0 ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-rose-100 text-rose-800"}`}>
                <span className="font-price">{netMargin}%</span> Margin
              </span>
            </div>
            <div className={`text-3xl font-price font-extrabold ${netProfit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700"}`}>
              ₹{netProfit.toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-muted-foreground mt-2 space-y-1">
              <div>• Gross Sales Revenue: <strong className="text-foreground font-price">₹{grossSalesRevenue.toLocaleString()}</strong></div>
              <div>• Purchase / Materials Cost: <strong className="text-amber-700 dark:text-amber-400 font-price">₹{totalCOGS.toLocaleString()}</strong></div>
              <div>• Courier / Shipping Outflow: <strong className="text-blue-700 dark:text-blue-400 font-price">₹{courierOutflow.toLocaleString()}</strong></div>
              <div>• Operational Expenses: <strong className="text-purple-700 dark:text-purple-400 font-price">₹{totalOperationalExpenses.toLocaleString()}</strong></div>
              <div>• Recorded Losses &amp; Damages: <strong className="text-rose-700 dark:text-rose-400 font-price">₹{totalLosses.toLocaleString()}</strong></div>
            </div>
          </div>
          <div className="pt-3 mt-3 border-t border-border text-xs text-muted-foreground flex justify-between">
            <span>50/50 Share per Partner:</span>
            <strong className="text-foreground font-mono">₹{halfProfit.toLocaleString()}</strong>
          </div>
        </div>

        {/* Partner Equity & Share Breakdown */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xs flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
              <span className="flex items-center gap-1.5"><UserCheck className="w-4 h-4 text-terracotta" /> Partner Capital &amp; Equity</span>
              <span className="text-[10px] text-muted-foreground">Shuvasis &amp; Sourav</span>
            </div>
            <div className="space-y-3 mt-3">
              {/* Shuvasis */}
              <div className="bg-muted/30 border border-border/80 rounded-xl p-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-foreground">Shuvasis</span>
                  <span className="font-mono text-xs font-bold text-terracotta">Claim: ₹{shuvasisEquity.toLocaleString()}</span>
                </div>
                <div className="text-[11px] text-muted-foreground flex justify-between mt-1">
                  <span>Invested: ₹{shuvasisInvested.toLocaleString()}</span>
                  <span>Net Capital: ₹{shuvasisNetCapital.toLocaleString()}</span>
                </div>
              </div>
              {/* Sourav */}
              <div className="bg-muted/30 border border-border/80 rounded-xl p-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-semibold text-foreground">Sourav</span>
                  <span className="font-mono text-xs font-bold text-terracotta">Claim: ₹{souravEquity.toLocaleString()}</span>
                </div>
                <div className="text-[11px] text-muted-foreground flex justify-between mt-1">
                  <span>Invested: ₹{souravInvested.toLocaleString()}</span>
                  <span>Net Capital: ₹{souravNetCapital.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-2 mt-2 text-[11px] text-muted-foreground text-center flex justify-between border-t border-border">
            <span>Overall Investment: <strong className="text-foreground">₹{totalInvestments.toLocaleString()}</strong></span>
            <span>Capital Injected: <strong className="text-terracotta font-bold">₹{totalCapitalInjected.toLocaleString()}</strong></span>
          </div>
        </div>
      </div>

      {/* 6 Key Stat Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard testid="stat-revenue" icon={IndianRupee} label="Gross Sales" value={`₹${grossSalesRevenue.toLocaleString("en-IN")}`} sub={`${totalCompletedOrders} completed orders`} />
        <StatCard testid="stat-bags-sold" icon={ShoppingBag} label="Total Bags Sold" value={`${totalQuantitySold.toLocaleString("en-IN")} Pcs`} sub={`Baseline ${openQtySold} + Live ${liveQtySold} pcs`} />
        <StatCard testid="stat-capital" icon={Wallet} label="Total Capital Injected" value={`₹${totalCapitalInjected.toLocaleString("en-IN")}`} sub={`Invested ₹${totalInvestments.toLocaleString()} − Draws ₹${totalWithdrawals.toLocaleString()}`} />
        <StatCard
          testid="stat-expenses"
          icon={Receipt}
          label="Total Expenses"
          value={`₹${totalAllExpenses.toLocaleString("en-IN")}`}
          sub={
            openCOGS > 0
              ? `Baseline ₹${(openCOGS + openExp).toLocaleString("en-IN")} + Live expenses ₹${liveExpenses.toLocaleString("en-IN")}`
              : `Baseline ₹${openExp.toLocaleString("en-IN")} + Live expenses ₹${liveExpenses.toLocaleString("en-IN")}`
          }
        />
        <StatCard testid="stat-shipping" icon={Truck} label="Total Shipping Cost" value={`₹${courierOutflow.toLocaleString("en-IN")}`} sub={`Baseline ₹${openShip.toLocaleString()} + Live orders ₹${validOrders.reduce((sum, o) => sum + orderShipping(o, products), 0).toLocaleString()}`} />
        <StatCard testid="stat-losses" icon={ShieldAlert} label="Total Losses & Damages" value={`₹${totalLosses.toLocaleString("en-IN")}`} sub={`Baseline ₹${openLosses.toLocaleString()} + Live losses ₹${liveLosses.toLocaleString()}`} tone={totalLosses > 0 ? "warn" : "ok"} />
      </div>

      {/* Performance & Revenue Trend Analytics Panel */}
      <div className="border border-border p-6 bg-card rounded-2xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
          <div>
            <h3 className="font-serif text-xl font-bold text-foreground">Operational &amp; Sales Performance Analytics</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Detailed metric calculations across Weekly, Monthly, 90 Days, Yearly &amp; Lifetime windows</p>
          </div>
          {/* Timeframe Selector Pills */}
          <div className="flex flex-wrap gap-1 bg-muted p-1 rounded-xl border border-border text-xs font-medium">
            {Object.entries(RANGES).map(([k, v]) => (
              <button
                key={k}
                data-testid={`range-tab-${k}`}
                onClick={() => setRange(k)}
                className={`px-3 py-1.5 rounded-lg transition-all ${range === k ? "bg-terracotta text-white font-bold shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Timeframe Key Metric Summary Cards */}
        <div>
          <div className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground mb-3 flex items-center justify-between">
            <span>Detailed Calculation Breakdown ({currentStats.label})</span>
            <span className="text-[11px] text-terracotta font-semibold font-mono">Active Timeframe</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-muted/40 border border-border/80 rounded-xl p-3.5 text-center">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Orders Count</span>
              <span className="text-xl font-bold font-price text-foreground">{currentStats.ordersCount}</span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">Orders in period</span>
            </div>
            <div className="bg-muted/40 border border-border/80 rounded-xl p-3.5 text-center">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Bags / Items Sold</span>
              <span className="text-xl font-bold font-price text-terracotta">{currentStats.bagsSold} Pcs</span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">Quantity sold</span>
            </div>
            <div className="bg-muted/40 border border-border/80 rounded-xl p-3.5 text-center">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Gross Sales</span>
              <span className="text-xl font-bold font-price text-emerald-700 dark:text-emerald-400">₹{currentStats.sales.toLocaleString("en-IN")}</span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">Total Revenue</span>
            </div>
            <div className="bg-muted/40 border border-border/80 rounded-xl p-3.5 text-center">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Shipping Outflow</span>
              <span className="text-xl font-bold font-price text-blue-700 dark:text-blue-400">₹{currentStats.shipping.toLocaleString("en-IN")}</span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">Courier costs</span>
            </div>
            <div className="bg-muted/40 border border-border/80 rounded-xl p-3.5 text-center">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">COGS / Materials</span>
              <span className="text-xl font-bold font-price text-amber-700 dark:text-amber-400">₹{currentStats.cogs.toLocaleString("en-IN")}</span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">Product purchase</span>
            </div>
            <div className="bg-muted/40 border border-border/80 rounded-xl p-3.5 text-center">
              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Net Profit ({currentStats.margin}%)</span>
              <span className={`text-xl font-bold font-price ${currentStats.profit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700"}`}>
                ₹{currentStats.profit.toLocaleString("en-IN")}
              </span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">Net Earnings</span>
            </div>
          </div>
        </div>


      </div>

      {/* Recent Orders */}
      <div className="border border-border p-6 bg-card rounded-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-serif text-xl">Recent Orders (Last 10)</h3>
          <span className="text-xs text-muted-foreground font-mono">{RANGES[range]}</span>
        </div>
        {filteredOrders.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">No orders in this range.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2.5 pr-4">Date</th>
                  <th className="py-2.5 pr-4">Mode</th>
                  <th className="py-2.5 pr-4">Customer</th>
                  <th className="py-2.5 pr-4 text-amber-700 dark:text-amber-400">Purchase</th>
                  <th className="py-2.5 pr-4 text-blue-700 dark:text-blue-400">Shipping</th>
                  <th className="py-2.5 pr-4">Grand Total</th>
                  <th className="py-2.5 pr-4 text-emerald-700 dark:text-emerald-400">Net Profit</th>
                  <th className="py-2.5 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.slice(0, 10).map((o) => {
                  const t = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : new Date(o.createdAt).getTime();
                  const isOff = o.orderMode === "Offline";
                  const cogs = orderPurchaseCost(o, products);
                  const ship = orderShipping(o, products);
                  const total = orderGrandTotal(o, products);
                  const prof = calculateSingleOrderProfit(o, products);
                  return (
                    <tr key={o.id} className="border-t border-border">
                      <td className="py-2.5 pr-4 font-mono text-xs">{new Date(t).toLocaleDateString("en-IN")}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                          isOff
                            ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200"
                            : "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200"
                        }`}>
                          {o.orderMode || "Online"}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 font-medium">{o.customerName}</td>
                      <td className="py-2.5 pr-4 font-mono text-xs text-amber-700 dark:text-amber-400 font-semibold">₹{cogs.toLocaleString("en-IN")}</td>
                      <td className="py-2.5 pr-4 font-mono text-xs text-blue-700 dark:text-blue-400">₹{ship.toLocaleString("en-IN")}</td>
                      <td className="py-2.5 pr-4 font-mono font-bold">₹{total.toLocaleString("en-IN")}</td>
                      <td className={`py-2.5 pr-4 font-mono font-bold ${prof >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                        ₹{prof.toLocaleString("en-IN")}
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_CONFIG[o.status]?.badge || "bg-muted text-foreground border-border"}`}>
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* In-Hand Cash Audit & Breakdown Modal */}
      {showCashAudit && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-fade-in">
          <div className="bg-card border border-border rounded-2xl w-full max-w-2xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowCashAudit(false)}
              className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground rounded-full hover:bg-muted transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2.5 mb-4">
              <div className="p-2.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 rounded-xl">
                <Calculator className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-serif text-2xl font-bold text-foreground">In-Hand Cash Audit</h3>
                <p className="text-xs text-muted-foreground">Comprehensive calculation formula and Firebase data synchronization</p>
              </div>
            </div>

            {/* Live Hero In-Hand Number */}
            <div className="p-4 bg-gradient-to-br from-amber-950 via-amber-900 to-terracotta text-white rounded-xl mb-5 flex items-center justify-between">
              <div>
                <div className="text-xs text-amber-200 font-semibold uppercase tracking-wider">Calculated Liquid In-Hand Cash</div>
                <div className="text-3xl font-price font-extrabold text-white mt-0.5">
                  ₹{inHandCash.toLocaleString("en-IN")}
                </div>
              </div>
              <div className="text-right text-xs text-amber-200/90 space-y-0.5">
                <div className="flex items-center justify-end gap-1.5 font-medium">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Firebase Connected
                </div>
                <div className="text-[11px] opacity-80">{orders.length} orders • {expenses.length} expenses</div>
              </div>
            </div>

            {/* Detailed Line Items */}
            <div className="space-y-4 text-xs">
              <div>
                <div className="font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider text-[11px] mb-2 flex items-center gap-1.5">
                  <ArrowUpRight className="w-3.5 h-3.5" /> Cash Inflows (+)
                </div>
                <div className="space-y-1.5 bg-muted/40 p-3 rounded-lg border border-border">
                  <div className="flex justify-between items-center py-1 border-b border-border/50">
                    <span className="text-muted-foreground">Starting Liquid Cash on Hand (Baseline)</span>
                    <span className="font-price font-semibold text-foreground">+₹{openCash.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-border/50">
                    <div>
                      <span className="text-muted-foreground">Net Partner Capital Injected</span>
                      <span className="block text-[10px] text-muted-foreground/80">Investments: ₹{totalInvestments.toLocaleString()} − Draws: ₹{totalWithdrawals.toLocaleString()}</span>
                    </div>
                    <span className="font-price font-semibold text-foreground">+₹{totalCapitalInjected.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-border/50">
                    <div>
                      <span className="text-muted-foreground">Direct Store Sales Collected</span>
                      <span className="block text-[10px] text-muted-foreground/80">{validOrders.length} valid orders received</span>
                    </div>
                    <span className="font-price font-semibold text-foreground">+₹{directSalesRevenue.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-border/50">
                    <span className="text-muted-foreground">Historical Baseline Sales Revenue</span>
                    <span className="font-price font-semibold text-foreground">+₹{openRev.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <div>
                      <span className="text-muted-foreground">Marketplace Bank Payouts Credited</span>
                      <span className="block text-[10px] text-muted-foreground/80">{marketplaceOrders.length} marketplace settlements</span>
                    </div>
                    <span className="font-price font-semibold text-foreground">+₹{totalMarketplacePayout.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>

              <div>
                <div className="font-semibold text-rose-700 dark:text-rose-400 uppercase tracking-wider text-[11px] mb-2 flex items-center gap-1.5">
                  <ArrowDownRight className="w-3.5 h-3.5" /> Cash Outflows (−)
                </div>
                <div className="space-y-1.5 bg-muted/40 p-3 rounded-lg border border-border">
                  <div className="flex justify-between items-center py-1 border-b border-border/50">
                    <div>
                      <span className="text-muted-foreground">Materials &amp; Production Cost (COGS)</span>
                      <span className="block text-[10px] text-muted-foreground/80">Opening COGS: ₹{openCOGS.toLocaleString()} + Live Materials: ₹{cogsExpenses.toLocaleString()}</span>
                    </div>
                    <span className="font-price font-semibold text-rose-600 dark:text-rose-400">−₹{totalCOGS.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-border/50">
                    <div>
                      <span className="text-muted-foreground">Operational Expenses &amp; Overheads</span>
                      <span className="block text-[10px] text-muted-foreground/80">Opening OpEx: ₹{openExp.toLocaleString()} + Live Overheads: ₹{operationalExpenses.toLocaleString()}</span>
                    </div>
                    <span className="font-price font-semibold text-rose-600 dark:text-rose-400">−₹{totalOperationalExpenses.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-border/50">
                    <div>
                      <span className="text-muted-foreground">Courier &amp; Delivery Shipping Outflow</span>
                      <span className="block text-[10px] text-muted-foreground/80">Opening Shipping: ₹{openShip.toLocaleString()} + Order Shipping: ₹{(courierOutflow - openShip).toLocaleString()}</span>
                    </div>
                    <span className="font-price font-semibold text-rose-600 dark:text-rose-400">−₹{courierOutflow.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <div>
                      <span className="text-muted-foreground">Business Losses &amp; Damaged Stock</span>
                      <span className="block text-[10px] text-muted-foreground/80">Opening Losses: ₹{openLosses.toLocaleString()} + Recorded Losses: ₹{liveLosses.toLocaleString()}</span>
                    </div>
                    <span className="font-price font-semibold text-rose-600 dark:text-rose-400">−₹{totalLosses.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cloud Sync Status and Refresh Action */}
            <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>Live sync with Firebase Firestore active.</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    setIsRefreshing(true);
                    if (refresh) {
                      try {
                        await refresh();
                        toast.success("Synchronized with Firebase Firestore!");
                      } catch (err) {
                        toast.error("Sync error: " + (err.message || "Failed"));
                      }
                    }
                    setIsRefreshing(false);
                  }}
                  disabled={isRefreshing}
                  className="px-3.5 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-medium text-foreground flex items-center gap-1.5 transition cursor-pointer"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                  {isRefreshing ? "Syncing..." : "Sync from Firebase"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCashAudit(false)}
                  className="px-4 py-1.5 rounded-lg bg-foreground text-background text-xs font-semibold hover:opacity-90 transition cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid, tone }) {
  return (
    <div data-testid={testid} className="border border-border p-5 bg-card">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-3">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className={`font-price text-3xl font-bold ${tone === "warn" ? "text-terracotta" : ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

// ============================================================================
// PRODUCTS
// ============================================================================
function ProductsPanel({ products, onNew, onEdit, onDelete, onGenerateLabel }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-foreground">{products.length} products</div>
        <div className="flex items-center gap-2">
          {onGenerateLabel && (
            <button
              type="button"
              data-testid="print-products-label-btn"
              onClick={() => onGenerateLabel(null)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border bg-background hover:bg-muted font-medium text-xs sm:text-sm text-foreground transition cursor-pointer"
              title="Generate & print barcode stickers with MRP"
            >
              <Barcode className="w-4 h-4 text-terracotta" />
              <span>Print Labels</span>
            </button>
          )}
          <button data-testid="add-product-btn" onClick={onNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta text-primary-foreground text-sm">
            <Plus className="w-4 h-4" /> Add product
          </button>
        </div>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price & MRP</th>
              <th className="px-4 py-3">Shipping</th>
              <th className="px-4 py-3">Variants</th>
              <th className="px-4 py-3">Total stock</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const baseMrp = p.mrp != null && p.mrp !== "" ? Number(p.mrp) : Number(p.price);
              const ship = Number(p.shippingCharge || 0);
              return (
                <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-t border-border">
                  <td className="px-4 py-3 flex items-center gap-3">
                    <img
                      src={normaliseImageUrl(primaryImage(p))}
                      className="w-10 h-10 object-cover rounded"
                      alt=""
                      referrerPolicy="no-referrer"
                      onError={(e) => handleImageError(e, primaryImage(p))}
                    />
                    <div>
                      <span className="line-clamp-1 font-medium">{p.name}</span>
                      {(p.bagType || p.material || p.size || p.weight) && (
                        <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground mt-0.5">
                          {p.bagType && <span className="bg-muted px-1 rounded">{p.bagType}</span>}
                          {p.material && <span>{p.material}</span>}
                          {p.size && <span className="font-mono">({p.size})</span>}
                          {p.weight && <span>· {p.weight}</span>}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">{p.category}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold">₹{p.price}</span>
                    {baseMrp > p.price && (
                      <span className="ml-1 text-xs text-muted-foreground line-through">₹{baseMrp}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {ship > 0 ? (
                      <span className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">₹{ship}</span>
                    ) : (
                      <span className="text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">Free</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.variants?.map((v, i) => (
                      <span key={i} className="inline-block text-xs bg-muted px-2 py-0.5 rounded mr-1 mb-1">
                        {v.tag} · {v.stock} pcs {v.price != null && v.price !== "" && Number(v.price) !== Number(p.price) ? `(₹${v.price})` : ""}
                      </span>
                    ))}
                  </td>
                  <td className="px-4 py-3">
                    {isSoldOut(p) ? <span className="text-destructive font-medium">SOLD OUT</span> : p.stock}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {onGenerateLabel && (
                      <button
                        type="button"
                        data-testid={`label-product-${p.id}`}
                        onClick={() => onGenerateLabel(p)}
                        className="p-2 hover:text-terracotta text-muted-foreground hover:bg-muted rounded transition cursor-pointer"
                        title="Generate Barcode & MRP Label"
                      >
                        <Barcode className="w-4 h-4" />
                      </button>
                    )}
                    <button data-testid={`edit-${p.id}`} onClick={() => onEdit(p)} className="p-2 hover:text-terracotta"><Edit3 className="w-4 h-4" /></button>
                    <button data-testid={`delete-${p.id}`} onClick={() => { if (confirm("Delete this product?")) onDelete(p.id); }} className="p-2 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductEditor({ initial, masterSkus = [], onClose, onSaved, onGenerateLabel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [uploadingVariant, setUploadingVariant] = useState(null);
  const [pendingUrl, setPendingUrl] = useState({}); // { [variantIdx]: "typed url" }
  const [driveConfig, setDriveConfig] = useState(getDriveFolderConfig());
  const [showDriveFolderEdit, setShowDriveFolderEdit] = useState(false);
  const [customFolderInput, setCustomFolderInput] = useState(driveConfig.folderUrl || "");
  const [driveAuthed, setDriveAuthed] = useState(hasDriveAccessToken());
  const [driveUserEmail, setDriveUserEmail] = useState(getDriveUserEmail());
  const [authorizingDrive, setAuthorizingDrive] = useState(false);
  const [pendingDriveUpload, setPendingDriveUpload] = useState(null); // { variantIdx, files }
  const [authError, setAuthError] = useState(null);

  // GST calculation mode: "inclusive" | "exclusive"
  const [productGstMode, setProductGstMode] = useState(
    initial?.gstMode || (initial?.priceIncludesGst !== false ? "inclusive" : "exclusive")
  );
  const isProdGstInclusive = productGstMode === "inclusive";

  const enteredPriceNum = Number(form.price || 0);
  const prodGstRateNum = Number(form.gstRate || 0);
  let taxableBasePrice = enteredPriceNum;
  let prodGstAmount = 0;
  let finalConsumerPrice = enteredPriceNum;

  if (prodGstRateNum > 0 && enteredPriceNum > 0) {
    if (isProdGstInclusive) {
      taxableBasePrice = Number((enteredPriceNum / (1 + prodGstRateNum / 100)).toFixed(2));
      prodGstAmount = Number((enteredPriceNum - taxableBasePrice).toFixed(2));
      finalConsumerPrice = enteredPriceNum;
    } else {
      taxableBasePrice = enteredPriceNum;
      prodGstAmount = Number(((enteredPriceNum * prodGstRateNum) / 100).toFixed(2));
      finalConsumerPrice = Number((enteredPriceNum + prodGstAmount).toFixed(2));
    }
  }

  // Master SKU picker / autofill states
  const [selectedMasterSkuId, setSelectedMasterSkuId] = useState("");
  const [masterSkuSearch, setMasterSkuSearch] = useState("");

  const filteredMasterSkus = useMemo(() => {
    if (!masterSkuSearch.trim()) return masterSkus;
    const q = masterSkuSearch.trim().toLowerCase();
    return masterSkus.filter((s) =>
      (s.sku || "").toLowerCase().includes(q) ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.category || "").toLowerCase().includes(q) ||
      (s.material || "").toLowerCase().includes(q)
    );
  }, [masterSkus, masterSkuSearch]);

  const handleApplyMasterSku = (skuObj) => {
    if (!skuObj) return;
    setForm((prev) => {
      const updatedVariants = prev.variants && prev.variants.length > 0
        ? prev.variants.map((v, i) => i === 0 ? {
            ...v,
            tag: skuObj.colour || v.tag || "Default",
            stock: skuObj.quantity != null ? String(skuObj.quantity) : v.stock,
            price: skuObj.saleCost != null ? String(skuObj.saleCost) : v.price,
            mrp: skuObj.mrp != null ? String(skuObj.mrp) : v.mrp,
            productCost: skuObj.purchaseCost != null ? String(skuObj.purchaseCost) : v.productCost,
            packagingCost: skuObj.additionalCost != null ? String(skuObj.additionalCost) : v.packagingCost,
          } : v)
        : [{
            tag: skuObj.colour || "Default",
            stock: String(skuObj.quantity || 10),
            comboQty: "1",
            price: String(skuObj.saleCost || 0),
            mrp: String(skuObj.mrp || 0),
            productCost: String(skuObj.purchaseCost || 0),
            packagingCost: String(skuObj.additionalCost || 80),
            extraCharge: "0",
            images: [],
          }];

      return {
        ...prev,
        sku: skuObj.sku,
        name: skuObj.name || prev.name,
        category: skuObj.category || prev.category,
        price: skuObj.saleCost != null ? String(skuObj.saleCost) : prev.price,
        mrp: skuObj.mrp != null ? String(skuObj.mrp) : prev.mrp,
        shippingCharge: skuObj.shippingCharge != null ? String(skuObj.shippingCharge) : prev.shippingCharge,
        gstRate: skuObj.gstRate != null ? String(skuObj.gstRate) : prev.gstRate,
        material: skuObj.material || prev.material,
        colour: skuObj.colour || prev.colour,
        size: skuObj.size || prev.size,
        weight: skuObj.weight || prev.weight,
        bagType: skuObj.bagType || prev.bagType,
        description: skuObj.description || prev.description,
        productCost: skuObj.purchaseCost != null ? String(skuObj.purchaseCost) : prev.productCost,
        packagingCost: skuObj.additionalCost != null ? String(skuObj.additionalCost) : prev.packagingCost,
        variants: updatedVariants,
      };
    });
    toast.success(`Autofilled product details from Master SKU: ${skuObj.sku}`);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSaveDriveFolder = (e) => {
    e.preventDefault();
    setDriveFolderConfig(customFolderInput);
    const updated = getDriveFolderConfig();
    setDriveConfig(updated);
    setShowDriveFolderEdit(false);
    toast.success("Google Drive target folder updated");
  };

  const handleConnectDrive = async (pending = null) => {
    setAuthorizingDrive(true);
    setAuthError(null);
    try {
      await authenticateGoogleDrive();
      setDriveAuthed(true);
      setDriveUserEmail(getDriveUserEmail());
      toast.success("Google Drive connected successfully!");

      const target = pending || pendingDriveUpload;
      if (target && target.files?.length) {
        setPendingDriveUpload(null);
        await executeUpload(target.variantIdx, target.files);
      }
    } catch (err) {
      console.error("Drive auth error:", err);
      if (err.isPopupBlocked || err.code === "auth/popup-blocked" || err.message?.includes("popup-blocked")) {
        setAuthError("popup-blocked");
        toast.error("Google popup blocked by browser. Please allow popups or open in a new tab.");
      } else {
        setAuthError(err.message);
        toast.error(err.message || "Failed to connect Google Drive");
      }
    } finally {
      setAuthorizingDrive(false);
    }
  };

  const handleDisconnectDrive = () => {
    disconnectGoogleDrive();
    setDriveAuthed(false);
    setDriveUserEmail(null);
    toast.info("Google Drive disconnected");
  };

  const updateVariant = (idx, key, value) => {
    setForm((f) => {
      const variants = f.variants.map((v, i) => i === idx ? { ...v, [key]: value } : v);
      return { ...f, variants };
    });
  };
  const addVariant = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), { tag: "", stock: "", comboQty: "1", price: "", mrp: "", images: [] }] }));
  const removeVariant = (idx) => setForm((f) => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

  const addVariantImageUrl = (idx, raw) => {
    if (!raw || !raw.trim()) return false;
    const converted = driveUrlToImage(raw.trim());
    if (converted !== raw.trim()) toast.success("Google Drive link converted to high-performance embed URL");
    setForm((f) => {
      const variants = f.variants.map((v, i) => i === idx ? { ...v, images: [...(v.images || []), converted] } : v);
      return { ...f, variants };
    });
    return true;
  };
  const commitPendingUrl = (idx) => {
    const val = pendingUrl[idx];
    if (addVariantImageUrl(idx, val)) {
      setPendingUrl((p) => ({ ...p, [idx]: "" }));
    }
  };
  const removeVariantImage = (vIdx, imgIdx) => {
    setForm((f) => {
      const variants = f.variants.map((v, i) => i === vIdx ? { ...v, images: v.images.filter((_, j) => j !== imgIdx) } : v);
      return { ...f, variants };
    });
  };

  const executeUpload = async (variantIdx, files) => {
    setBusy(true);
    setUploadingVariant(variantIdx);
    try {
      const urls = await Promise.all(files.map(uploadProductImage));
      setForm((f) => {
        const variants = f.variants.map((v, i) => i === variantIdx ? { ...v, images: [...(v.images || []), ...urls] } : v);
        return { ...f, variants };
      });
      toast.success(`${files.length} image(s) uploaded directly to Google Drive!`);
    } catch (err) {
      console.error("Google Drive upload error:", err);
      if (err.code === "auth/popup-blocked" || err.isPopupBlocked || err.message?.includes("popup-blocked")) {
        setAuthError("popup-blocked");
        setPendingDriveUpload({ variantIdx, files });
      } else if (err.message?.includes("403") || err.message?.includes("insufficient") || err.message?.includes("401")) {
        setDriveAuthed(false);
        setDriveUserEmail(null);
        setPendingDriveUpload({ variantIdx, files });
        toast.error("Google Drive permissions upgraded. Please click 'Reconnect Google Drive' to grant access.");
      } else {
        toast.error(err.message || "Google Drive upload failed. Please verify authorization.");
      }
    } finally {
      setBusy(false);
      setUploadingVariant(null);
    }
  };

  const onVariantFile = (idx) => async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = ""; // Reset input so same file selection re-triggers change if needed

    // If Google Drive token is not yet cached in memory, prompt user via direct button click
    // to prevent browsers from blocking window.open inside the asynchronous input onchange event
    if (!hasDriveAccessToken()) {
      setPendingDriveUpload({ variantIdx: idx, files });
      return;
    }

    await executeUpload(idx, files);
  };

  const save = async () => {
    if (!form.name || !form.price) return toast.error("Name and price are required");
    const cleanHsn = (form.hsn || form.hsnCode || "").trim();
    if (!cleanHsn) return toast.error("HSN code is mandatory (e.g. 4202)");
    if (!form.variants?.length) return toast.error("Add at least one variant");
    if (form.variants.some((v) => !v.tag)) return toast.error("Each variant needs a tag/colour label");

    // Auto-commit any URLs the admin typed but didn't Add
    let basePrice = Number(form.price || 0);
    let baseMrp = form.mrp !== "" && form.mrp != null ? Number(form.mrp) : basePrice;
    let shippingCharge = Number(form.shippingCharge || 0);

    let variants = form.variants.map((v) => {
      const vPrice = v.price !== "" && v.price != null ? Number(v.price) : basePrice;
      const vMrp = v.mrp !== "" && v.mrp != null ? Number(v.mrp) : (vPrice > basePrice ? vPrice : baseMrp);
      const comboQty = parseComboQty(v.comboQty);
      return {
        ...v,
        stock: Number(v.stock || 0),
        comboQty,
        price: vPrice,
        mrp: vMrp,
        images: [...(v.images || [])]
      };
    });

    Object.entries(pendingUrl).forEach(([idx, raw]) => {
      if (raw && raw.trim()) {
        const converted = driveUrlToImage(raw.trim());
        if (variants[idx]) variants[idx].images.push(converted);
      }
    });

    // Guardrail: warn if some variants still have zero images
    const emptyVars = variants.filter((v) => (v.images || []).length === 0).map((v) => v.tag);
    if (emptyVars.length) {
      const ok = window.confirm(`These variants have no images: ${emptyVars.join(", ")}.\n\nSave anyway?`);
      if (!ok) return;
    }
    setBusy(true);
    try {
      const cleanSku = (form.sku || "").trim().toUpperCase();
      await saveProduct({
        ...form,
        sku: cleanSku,
        hsn: cleanHsn,
        hsnCode: cleanHsn,
        price: basePrice,
        mrp: baseMrp,
        shippingCharge,
        gstRate: Number(form.gstRate ?? 0),
        gstMode: productGstMode,
        priceIncludesGst: isProdGstInclusive,
        variants,
      });

      // Keep Master SKU inventory in sync if a custom SKU ID is provided
      if (cleanSku) {
        try {
          await saveMasterSku({
            sku: cleanSku,
            name: form.name.trim(),
            hsn: cleanHsn,
            hsnCode: cleanHsn,
            category: form.category || "Leather Bags",
            purchaseCost: Number(form.productCost || 0),
            saleCost: basePrice,
            mrp: baseMrp,
            quantity: Number(variants[0]?.stock || 0),
            additionalCost: Number(form.packagingCost || 80),
            gstRate: Number(form.gstRate || 0),
            shippingCharge,
            description: form.description || "",
            material: form.material || "",
            colour: form.colour || "",
            size: form.size || "",
            weight: form.weight || "",
            bagType: form.bagType || "",
          });
        } catch (e) {
          console.warn("Could not sync with Master SKU:", e);
        }
      }

      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(err.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
      <div className="bg-background w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-border">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background z-10">
          <h3 className="font-serif text-2xl">{form.id ? "Edit product" : "New product"}</h3>
          <button data-testid="pe-close" onClick={onClose} className="p-2"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Google Drive Storage Target Banner */}
          <div className="bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-900 rounded-lg p-3 text-xs flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sky-900 dark:text-sky-200 font-medium">
                <HardDrive className="w-4 h-4 text-sky-600 dark:text-sky-400 shrink-0" />
                <span>Product images upload directly to Google Drive (no Firestore image storage)</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={driveConfig.folderUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline font-mono text-[11px]"
                >
                  <Folder className="w-3.5 h-3.5" />
                  Open Folder
                  <ExternalLink className="w-3 h-3" />
                </a>
                <button
                  type="button"
                  onClick={() => setShowDriveFolderEdit(!showDriveFolderEdit)}
                  className="px-2 py-0.5 rounded border border-sky-300 dark:border-sky-800 bg-white/60 dark:bg-black/30 text-sky-800 dark:text-sky-200 text-[11px] hover:bg-sky-100"
                >
                  {showDriveFolderEdit ? "Close" : "Change Folder"}
                </button>
              </div>
            </div>

            {showDriveFolderEdit ? (
              <form onSubmit={handleSaveDriveFolder} className="pt-2 border-t border-sky-200/80 dark:border-sky-800/80 flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={customFolderInput}
                  onChange={(e) => setCustomFolderInput(e.target.value)}
                  placeholder="Paste Google Drive folder URL or ID"
                  className="flex-1 bg-white dark:bg-zinc-900 border border-sky-300 dark:border-sky-800 px-3 py-1.5 rounded text-xs font-mono outline-none"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-3 py-1.5 bg-sky-700 text-white rounded text-xs font-medium hover:bg-sky-800"
                  >
                    Save Folder
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDriveFolderConfig("");
                      const def = getDriveFolderConfig();
                      setDriveConfig(def);
                      setCustomFolderInput(def.folderUrl);
                      setShowDriveFolderEdit(false);
                      toast.success("Reset to default Drive folder");
                    }}
                    className="px-3 py-1.5 border border-sky-300 dark:border-sky-800 rounded text-xs hover:bg-sky-100/50"
                  >
                    Reset Default
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-[11px] text-sky-700/80 dark:text-sky-300/80 flex items-center gap-1.5">
                <span className="font-semibold">Target Folder:</span>
                <span className="font-mono truncate max-w-md">{driveConfig.folderUrl}</span>
              </div>
            )}

            {/* Google Drive Authorization Status & Connect Button */}
            <div className="pt-2 border-t border-sky-200/80 dark:border-sky-800/80 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sky-900 dark:text-sky-200">Google Drive Access:</span>
                {driveAuthed ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 font-medium text-[11px]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    Connected {driveUserEmail ? `(${driveUserEmail})` : ""}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 font-medium text-[11px]">
                    Not connected yet
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {driveAuthed ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={authorizingDrive}
                      onClick={() => handleConnectDrive()}
                      className="text-[11px] text-terracotta hover:underline font-medium"
                      title="Refresh or upgrade Google Drive access token"
                    >
                      {authorizingDrive ? "Refreshing…" : "Reconnect"}
                    </button>
                    <span className="text-muted-foreground text-[10px]">•</span>
                    <button
                      type="button"
                      onClick={handleDisconnectDrive}
                      className="text-[11px] text-muted-foreground hover:text-destructive underline"
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={authorizingDrive}
                    onClick={() => handleConnectDrive()}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-white dark:bg-zinc-800 border border-sky-300 dark:border-sky-700 text-sky-900 dark:text-sky-100 font-medium text-[11px] hover:bg-sky-50 dark:hover:bg-zinc-700 shadow-2xs transition disabled:opacity-50"
                  >
                    {authorizingDrive ? (
                      <RefreshCw className="w-3 h-3 animate-spin text-terracotta" />
                    ) : (
                      <svg className="w-3.5 h-3.5" viewBox="0 0 48 48">
                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                      </svg>
                    )}
                    <span>{authorizingDrive ? "Connecting…" : "Sign in with Google"}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Master SKU Autofill Picker */}
          {masterSkus.length > 0 && (
            <div className="bg-terracotta/5 border border-terracotta/20 rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 rounded-md bg-terracotta text-white">
                    <Boxes className="w-4 h-4" />
                  </span>
                  <div>
                    <div className="text-xs font-bold text-foreground">
                      Autofill from Master SKU Catalog
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Select or search any custom SKU ID to auto-populate product name, costs, pricing, tax, specs &amp; stock
                    </div>
                  </div>
                </div>
                {form.sku && (
                  <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded bg-terracotta/10 text-terracotta border border-terracotta/20">
                    SKU: {form.sku}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-5 relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    data-testid="pe-master-sku-search"
                    value={masterSkuSearch}
                    onChange={(e) => setMasterSkuSearch(e.target.value)}
                    placeholder="Search SKU ID (e.g. LC-BAG-001)..."
                    className="w-full bg-background border border-border pl-8 pr-7 py-2 rounded-lg text-xs outline-none focus:border-terracotta"
                  />
                  {masterSkuSearch && (
                    <button
                      type="button"
                      onClick={() => setMasterSkuSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="sm:col-span-7">
                  <select
                    data-testid="pe-master-sku-dropdown"
                    value={selectedMasterSkuId}
                    onChange={(e) => {
                      const skuId = e.target.value;
                      setSelectedMasterSkuId(skuId);
                      const target = masterSkus.find((s) => s.id === skuId || s.sku === skuId);
                      if (target) handleApplyMasterSku(target);
                    }}
                    className="w-full bg-background border border-border px-3 py-2 rounded-lg text-xs outline-none focus:border-terracotta font-mono font-medium"
                  >
                    <option value="">-- Choose Master SKU to Autofill Details --</option>
                    {filteredMasterSkus.map((s) => (
                      <option key={s.id} value={s.id}>
                        [{s.sku}] {s.name} • Stock: {s.quantity} • Cost: ₹{s.purchaseCost} • Sale: ₹{s.saleCost}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Input testid="pe-name" label="Product Name *" value={form.name} onChange={set("name")} placeholder="e.g. Santiniketan Leather Classic Tote" />
            </div>
            <div>
              <Input
                testid="pe-sku"
                label="Custom SKU ID"
                value={form.sku || ""}
                onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
                placeholder="e.g. LC-BAG-001"
              />
            </div>
            <div>
              <Input
                testid="pe-hsn"
                label="HSN Code *"
                required
                value={form.hsn || form.hsnCode || ""}
                onChange={(e) => setForm({ ...form, hsn: e.target.value.trim(), hsnCode: e.target.value.trim() })}
                placeholder="e.g. 4202"
              />
            </div>
          </div>

          {/* Product Specifications */}
          <div className="bg-muted/30 border border-border/80 rounded-lg p-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-terracotta">Product Specifications</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                testid="pe-bag-type"
                label="Bag Type"
                value={form.bagType || ""}
                onChange={set("bagType")}
                placeholder="e.g. Tote Bag, Sling / Crossbody, Handbag"
              />
              <Input
                testid="pe-material"
                label="Material"
                value={form.material || ""}
                onChange={set("material")}
                placeholder="e.g. Vegetable-Tanned Finish Leather, Handloom Cotton"
              />
              <Input
                testid="pe-colour"
                label="Colour / Shade"
                value={form.colour || ""}
                onChange={set("colour")}
                placeholder="e.g. Tan Brown & Maroon, Multi-color Batik"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                testid="pe-size"
                label="Product Size / Dimensions"
                value={form.size || ""}
                onChange={set("size")}
                placeholder='e.g. 14" H × 12" W × 4" D  or  35 × 30 × 10 cm'
              />
              <Input
                testid="pe-weight"
                label="Weight"
                value={form.weight || ""}
                onChange={set("weight")}
                placeholder="e.g. 450g  or  0.45 kg"
              />
            </div>
          </div>

          <Input testid="pe-desc" label="Description" value={form.description} onChange={set("description")} as="textarea" placeholder="Detailed product story, craftsmanship, artisan techniques, interior pockets, zippers, and care instructions..." />
          <Input
            testid="pe-video"
            label="YouTube video URL (optional)"
            type="url"
            value={form.videoUrl || ""}
            onChange={set("videoUrl")}
            placeholder="https://www.youtube.com/watch?v=…  or  https://youtu.be/…"
          />

          {/* Pricing, GST Mode & Tax Calculation Section */}
          <div className="bg-muted/30 border border-border rounded-lg p-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Pricing &amp; GST Calculation
              </span>
              <div className="flex items-center gap-1 bg-background p-0.5 rounded border border-border text-xs">
                <span className="text-[10px] text-muted-foreground px-1.5 font-medium">GST:</span>
                <button
                  type="button"
                  data-testid="pe-toggle-gst-inclusive"
                  onClick={() => setProductGstMode("inclusive")}
                  className={`px-2 py-1 rounded font-medium transition cursor-pointer text-[11px] ${
                    isProdGstInclusive ? "bg-terracotta text-white shadow-xs font-semibold" : "text-muted-foreground hover:text-foreground"
                  }`}
                  title="Entered price already includes GST (Retail MRP)"
                >
                  Include GST
                </button>
                <button
                  type="button"
                  data-testid="pe-toggle-gst-exclusive"
                  onClick={() => setProductGstMode("exclusive")}
                  className={`px-2 py-1 rounded font-medium transition cursor-pointer text-[11px] ${
                    !isProdGstInclusive ? "bg-terracotta text-white shadow-xs font-semibold" : "text-muted-foreground hover:text-foreground"
                  }`}
                  title="Entered price is base taxable price; GST is calculated on top"
                >
                  Exclude GST (+Tax)
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
              <Input
                testid="pe-price"
                label={isProdGstInclusive ? "Price (Incl. GST) ₹ *" : "Base Price (Excl. GST) ₹ *"}
                type="number"
                value={form.price}
                onChange={set("price")}
                placeholder="e.g. 499"
              />
              <Input
                testid="pe-mrp"
                label="Base MRP ₹"
                type="number"
                value={form.mrp || ""}
                onChange={set("mrp")}
                placeholder="e.g. 799"
              />
              <Input
                testid="pe-shipping"
                label="Shipping Charge ₹"
                type="number"
                value={form.shippingCharge ?? "0"}
                onChange={set("shippingCharge")}
                placeholder="0 for Free"
              />
              <label className="block">
                <span className="block text-sm mb-1.5 text-muted-foreground">Tax Rate (GST %)</span>
                <select
                  data-testid="pe-tax"
                  value={form.gstRate ?? "0"}
                  onChange={set("gstRate")}
                  className="w-full bg-muted/50 border border-border px-3 py-3 rounded-md text-sm outline-none focus:border-foreground"
                >
                  <option value="0">0% GST (Default)</option>
                  <option value="5">5% GST</option>
                  <option value="12">12% GST</option>
                  <option value="18">18% GST</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-sm mb-1.5 text-muted-foreground">Category</span>
                <select
                  data-testid="pe-cat"
                  value={form.category}
                  onChange={set("category")}
                  className="w-full bg-muted/50 border border-border px-3 py-3 rounded-md text-sm outline-none focus:border-foreground"
                >
                  {CATS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
            </div>

            {/* Live GST Breakdown Pill */}
            {enteredPriceNum > 0 && prodGstRateNum > 0 && (
              <div className="px-3 py-1.5 rounded bg-background border border-border text-xs flex flex-wrap items-center justify-between gap-2">
                <span className="text-muted-foreground">
                  Taxable Base: <strong className="text-foreground font-price">₹{taxableBasePrice}</strong> + GST ({prodGstRateNum}%): <strong className="text-emerald-700 dark:text-emerald-400 font-price">₹{prodGstAmount}</strong> = Consumer Total: <strong className="text-terracotta font-price">₹{finalConsumerPrice}</strong>
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase font-semibold">
                  {isProdGstInclusive ? "GST-Inclusive" : "GST-Exclusive"}
                </span>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Variants / Colours / Combos *</span>
              <button data-testid="pe-add-variant" type="button" onClick={addVariant} className="text-xs inline-flex items-center gap-1 px-3 py-1 rounded-full border border-border hover:bg-muted">
                <Plus className="w-3 h-3 text-terracotta" /> Add variant
              </button>
            </div>
            <div className="space-y-4">
              {form.variants.map((v, vIdx) => (
                <div key={vIdx} data-testid={`variant-row-${vIdx}`} className="border border-border p-4 bg-muted/20 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
                    <label className="block sm:col-span-1">
                      <span className="block text-xs mb-1 text-muted-foreground">Tag / Colour *</span>
                      <input data-testid={`var-tag-${vIdx}`} value={v.tag} onChange={(e) => updateVariant(vIdx, "tag", e.target.value)}
                        placeholder="e.g. Red, Combo Pack"
                        className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm" />
                    </label>
                    <label className="block">
                      <span className="block text-xs mb-1 text-muted-foreground">Stock *</span>
                      <input data-testid={`var-stock-${vIdx}`} type="number" min="0" value={v.stock} onChange={(e) => updateVariant(vIdx, "stock", e.target.value)}
                        className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm" />
                    </label>
                    <label className="block">
                      <span className="block text-xs mb-1 text-muted-foreground" title="Quantity of items included in this variant / combo pack">Combo Qty (Pcs) *</span>
                      <input data-testid={`var-combo-${vIdx}`} type="number" min="1" value={v.comboQty ?? "1"} onChange={(e) => updateVariant(vIdx, "comboQty", e.target.value)}
                        placeholder="1"
                        className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm font-price font-semibold" />
                    </label>
                    <label className="block">
                      <span className="block text-xs mb-1 text-muted-foreground">Price ₹ (optional)</span>
                      <input data-testid={`var-price-${vIdx}`} type="number" min="0" value={v.price ?? ""} onChange={(e) => updateVariant(vIdx, "price", e.target.value)}
                        placeholder={`Base (₹${form.price || 0})`}
                        className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm" />
                    </label>
                    <div className="flex gap-2 items-end">
                      <label className="block flex-1">
                        <span className="block text-xs mb-1 text-muted-foreground">MRP ₹ (optional)</span>
                        <input data-testid={`var-mrp-${vIdx}`} type="number" min="0" value={v.mrp ?? ""} onChange={(e) => updateVariant(vIdx, "mrp", e.target.value)}
                          placeholder={`Base (₹${form.mrp || form.price || 0})`}
                          className="w-full bg-background border border-border px-3 py-2 rounded-md text-sm" />
                      </label>
                      {form.variants.length > 1 && (
                        <button data-testid={`var-remove-${vIdx}`} type="button" onClick={() => removeVariant(vIdx)}
                          className="p-2 text-muted-foreground hover:text-destructive pb-2.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground mb-2">Images for this variant</div>
                    <div className="grid grid-cols-5 gap-2 mb-2">
                      {(v.images || []).map((im, i) => (
                        <div key={i} className="relative aspect-square">
                          <img
                            src={normaliseImageUrl(im)}
                            className="w-full h-full object-cover rounded"
                            alt=""
                            referrerPolicy="no-referrer"
                            onError={(e) => handleImageError(e, im)}
                          />
                          <button type="button" onClick={() => removeVariantImage(vIdx, i)} className="absolute top-1 right-1 bg-background/90 p-1 rounded-full"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <label className={`aspect-square border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:bg-muted transition text-center p-1 ${uploadingVariant === vIdx ? "opacity-60 pointer-events-none bg-sky-50 dark:bg-sky-950/20" : ""}`}>
                        <input data-testid={`var-upload-${vIdx}`} type="file" accept="image/*" multiple className="hidden" disabled={busy} onChange={onVariantFile(vIdx)} />
                        {uploadingVariant === vIdx ? (
                          <div className="flex flex-col items-center gap-1 text-sky-600 dark:text-sky-400">
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            <span className="text-[9px] font-medium leading-tight">Uploading to Drive…</span>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-muted-foreground mb-1" />
                            <span className="text-[10px] text-muted-foreground font-medium leading-tight">Upload to Drive</span>
                          </>
                        )}
                      </label>
                    </div>
                    <div className="rounded-md bg-background border border-border p-2">
                      <div className="flex items-center gap-2 text-xs mb-1">
                        <LinkIcon className="w-3 h-3 text-terracotta" />
                        Paste image URL (Google Drive supported) — press <strong>Enter</strong> or click <strong>Add</strong>
                      </div>
                      <div className="flex gap-2">
                        <input
                          data-testid={`var-url-${vIdx}`}
                          type="url"
                          placeholder="https://drive.google.com/file/d/…/view"
                          value={pendingUrl[vIdx] || ""}
                          onChange={(e) => setPendingUrl((p) => ({ ...p, [vIdx]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); commitPendingUrl(vIdx); }
                          }}
                          onBlur={() => commitPendingUrl(vIdx)}
                          className="flex-1 bg-muted/50 border border-border px-3 py-1.5 rounded text-sm outline-none focus:border-foreground"
                        />
                        <button
                          type="button"
                          data-testid={`var-url-add-${vIdx}`}
                          onClick={() => commitPendingUrl(vIdx)}
                          disabled={!pendingUrl[vIdx]}
                          className="px-3 py-1.5 rounded bg-terracotta text-primary-foreground text-sm disabled:opacity-40"
                        >
                          Add
                        </button>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {(v.images || []).length === 0
                          ? <span className="text-destructive">⚠ No images added yet for this variant</span>
                          : `${(v.images || []).length} image(s) added`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 border-t border-border flex flex-wrap items-center justify-between gap-3 sticky bottom-0 bg-background z-20">
          <button
            type="button"
            data-testid="pe-add-variant-bottom"
            onClick={addVariant}
            className="px-4 py-2 rounded-full border border-border hover:bg-muted font-medium text-xs sm:text-sm inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4 text-terracotta" /> Add Variant
          </button>
          <div className="flex items-center gap-2">
            {onGenerateLabel && (
              <button
                type="button"
                data-testid="pe-print-label"
                onClick={() => onGenerateLabel(form)}
                className="px-4 py-2 rounded-full border border-border hover:bg-muted font-medium text-xs sm:text-sm inline-flex items-center gap-1.5 text-foreground transition cursor-pointer"
                title="Generate Barcode & MRP Label for this product"
              >
                <Barcode className="w-4 h-4 text-terracotta" />
                <span>Print Label</span>
              </button>
            )}
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-full border border-border text-sm">Cancel</button>
            <button data-testid="pe-save" disabled={busy} onClick={save} className="px-5 py-2 rounded-full bg-terracotta text-primary-foreground text-sm font-medium disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>

      {/* Pending Google Drive Upload Prompt Modal (Direct user-click avoids auth/popup-blocked) */}
      {pendingDriveUpload && (
        <div className="fixed inset-0 z-60 bg-foreground/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-background border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-4 text-center animate-in fade-in zoom-in-95 duration-150">
            <div className="w-12 h-12 rounded-full bg-sky-100 dark:bg-sky-950 flex items-center justify-center mx-auto text-sky-600 dark:text-sky-400">
              <HardDrive className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-serif text-lg font-bold text-foreground">Connect Google Drive</h4>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                To upload your selected {pendingDriveUpload.files?.length || 1} image(s) directly to your Google Drive folder, please sign in with your Google account:
              </p>
            </div>

            {authError === "popup-blocked" ? (
              <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 p-3 rounded-lg text-xs text-amber-900 dark:text-amber-200 text-left space-y-1.5">
                <div className="font-semibold flex items-center gap-1.5 text-amber-800 dark:text-amber-300">
                  <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
                  Pop-up was blocked by browser
                </div>
                <p className="text-[11px] leading-relaxed">
                  Your browser blocked the Google Sign-in window. To fix:
                </p>
                <ul className="list-disc list-inside text-[11px] space-y-0.5 ml-1">
                  <li>Click the pop-up icon in your browser address bar and choose <strong>Always allow pop-ups</strong>.</li>
                  <li>Or open the app in a new tab using the preview open button in the top right.</li>
                </ul>
              </div>
            ) : authError ? (
              <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-800 p-3 rounded-lg text-xs text-rose-900 dark:text-rose-200 text-left space-y-2">
                <div className="font-semibold flex items-center gap-1.5 text-rose-800 dark:text-rose-300">
                  <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0" />
                  Google Sign-In Notice
                </div>
                <p className="text-[11px] leading-relaxed break-words font-mono bg-white/70 dark:bg-black/40 p-1.5 rounded border border-rose-200 dark:border-rose-900/50">
                  {typeof authError === "string" ? authError : authError.message || "Failed to authenticate with Google."}
                </p>
                <div className="pt-1 text-[11px] text-muted-foreground border-t border-rose-200/60 dark:border-rose-900/40 flex flex-col gap-1">
                  <span><strong>Workaround:</strong> You can also drop photos directly into your Drive folder and paste the link in the variant input:</span>
                  <a
                    href={driveConfig.folderUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 font-medium hover:underline text-[11px]"
                  >
                    <Folder className="w-3.5 h-3.5" />
                    Open Google Drive Folder
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ) : null}

            <div className="pt-2 flex flex-col gap-2">
              <button
                type="button"
                disabled={authorizingDrive}
                onClick={() => handleConnectDrive(pendingDriveUpload)}
                className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium text-xs hover:bg-zinc-50 dark:hover:bg-zinc-700 transition shadow-xs disabled:opacity-50"
              >
                {authorizingDrive ? (
                  <RefreshCw className="w-4 h-4 animate-spin text-terracotta" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 48 48">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                )}
                <span>{authorizingDrive ? "Opening Google Sign-in…" : "Sign in with Google to Authorize & Upload"}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setPendingDriveUpload(null);
                  setAuthError(null);
                }}
                className="w-full text-xs text-muted-foreground hover:text-foreground py-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Input({ label, testid, as, ...rest }) {
  const cls = "w-full bg-muted/50 border border-border px-4 py-3 rounded-md outline-none focus:border-foreground";
  return (
    <label className="block">
      <span className="block text-sm mb-1.5 text-muted-foreground">{label}</span>
      {as === "textarea" ? <textarea data-testid={testid} rows={3} {...rest} className={cls} /> : <input data-testid={testid} {...rest} className={cls} />}
    </label>
  );
}

// ============================================================================
// ORDERS
// ============================================================================
function OrdersPanel({ orders = [], products = [], marketplaceOrders = [], masterSkus = [], refresh }) {
  const [subTab, setSubTab] = useState("direct"); // "direct" | "marketplace"
  const [range, setRange] = useState("30");
  const [status, setStatus] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showOfflineModal, setShowOfflineModal] = useState(false);

  // Edit / Delete Order Modals states
  const [editingItemsOrder, setEditingItemsOrder] = useState(null);
  const [editingCustomerOrder, setEditingCustomerOrder] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);

  // Marketplace states
  const [showMarketplaceModal, setShowMarketplaceModal] = useState(false);
  const [editingMarketplaceOrder, setEditingMarketplaceOrder] = useState(null);
  const [mpFilterMarketplace, setMpFilterMarketplace] = useState("all");
  const [mpFilterRange, setMpFilterRange] = useState("all");
  const [mpFilterStatus, setMpFilterStatus] = useState("all");

  const filtered = useMemo(() => {
    const days = range === "all" ? null : Number(range);
    const cutoff = days != null ? Date.now() - days * 86400_000 : null;
    const q = searchQuery.trim().toLowerCase();

    return orders.filter((o) => {
      if (cutoff != null) {
        const t = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : new Date(o.createdAt).getTime();
        if (t < cutoff) return false;
      }
      if (status !== "all" && o.status !== status) return false;
      const oMode = o.orderMode || "Online";
      if (modeFilter !== "all" && oMode !== modeFilter) return false;

      if (q) {
        const name = (o.customerName || "").toLowerCase();
        const phone = (o.phone || "").toLowerCase();
        const id = (o.id || "").toLowerCase();
        const email = (o.email || "").toLowerCase();
        const city = (o.city || "").toLowerCase();
        const state = (o.state || "").toLowerCase();
        if (
          !name.includes(q) &&
          !phone.includes(q) &&
          !id.includes(q) &&
          !email.includes(q) &&
          !city.includes(q) &&
          !state.includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [orders, range, status, modeFilter, searchQuery]);

  // Filtered Marketplace Orders
  const filteredMarketplaceOrders = useMemo(() => {
    return marketplaceOrders.filter((m) => {
      if (mpFilterMarketplace !== "all") {
        if (mpFilterMarketplace === "Other") {
          if (m.marketplace !== "Other") return false;
        } else if (m.marketplace !== mpFilterMarketplace) {
          return false;
        }
      }
      if (mpFilterStatus !== "all" && m.status !== mpFilterStatus) return false;
      if (mpFilterRange !== "all") {
        const days = Number(mpFilterRange);
        const cutoff = Date.now() - days * 86400_000;
        const dStr = m.startDate || m.payoutDate || m.createdAt;
        const t = dStr ? new Date(dStr).getTime() : 0;
        if (t < cutoff) return false;
      }
      return true;
    });
  }, [marketplaceOrders, mpFilterMarketplace, mpFilterRange, mpFilterStatus]);

  // Marketplace Stats Summaries
  const mpStats = useMemo(() => {
    const gross = filteredMarketplaceOrders.reduce((s, m) => s + Number(m.grossSales || 0), 0);
    const netPayout = filteredMarketplaceOrders.reduce((s, m) => s + Number(m.netPayout || 0), 0);
    const deductions = filteredMarketplaceOrders.reduce(
      (s, m) =>
        s +
        Number(m.commissionFee || 0) +
        Number(m.logisticsFee || 0) +
        Number(m.taxDeductions || 0) +
        Number(m.otherDeductions || 0),
      0
    );
    const cogs = filteredMarketplaceOrders.reduce((s, m) => s + Number(m.cogs || 0), 0);
    const profit = filteredMarketplaceOrders.reduce((s, m) => s + Number(m.netProfit || 0), 0);
    const units = filteredMarketplaceOrders.reduce((s, m) => s + Number(m.unitsSold || 0), 0);
    return { gross, netPayout, deductions, cogs, profit, units };
  }, [filteredMarketplaceOrders]);

  const directSummary = useMemo(() => {
    const totalSales = filtered.reduce((s, o) => s + orderGrandTotal(o, products), 0);
    const totalPurchase = filtered.reduce((s, o) => s + orderPurchaseCost(o, products), 0);
    const totalShipping = filtered.reduce((s, o) => s + orderShipping(o, products), 0);
    const totalTax = filtered.reduce((s, o) => s + orderTaxTotal(o), 0);
    const totalProfit = filtered.reduce((s, o) => s + calculateSingleOrderProfit(o, products), 0);
    return { totalSales, totalPurchase, totalShipping, totalTax, totalProfit };
  }, [filtered, products]);

  return (
    <div className="space-y-6">
      {/* Sub-tab Switcher Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-3">
        <div className="flex items-center gap-2 bg-muted p-1 rounded-full border border-border">
          <button
            type="button"
            data-testid="subtab-direct-orders"
            onClick={() => setSubTab("direct")}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition flex items-center gap-2 ${
              subTab === "direct"
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <ShoppingBag className="w-3.5 h-3.5 text-terracotta" />
            Direct Orders (Online &amp; Offline)
            <span className="px-2 py-0.5 rounded-full text-[10px] bg-terracotta/15 text-terracotta font-mono">
              {orders.length}
            </span>
          </button>

          <button
            type="button"
            data-testid="subtab-marketplace-orders"
            onClick={() => setSubTab("marketplace")}
            className={`px-4 py-2 rounded-full text-xs font-semibold transition flex items-center gap-2 ${
              subTab === "marketplace"
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Building2 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
            Marketplace Orders / Settlements
            <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-300 font-mono">
              {marketplaceOrders.length}
            </span>
          </button>
        </div>

        {subTab === "marketplace" && (
          <button
            type="button"
            data-testid="add-marketplace-order-btn"
            onClick={() => {
              setEditingMarketplaceOrder(null);
              setShowMarketplaceModal(true);
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-terracotta text-primary-foreground text-xs font-medium hover:opacity-90 transition shadow-sm"
          >
            <Plus className="w-4 h-4" /> Add Marketplace Order / Settlement
          </button>
        )}
      </div>

      {subTab === "direct" ? (
        /* Direct Orders (Online & Offline) Section */
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground">{filtered.length} of {orders.length} orders</div>
              <button
                data-testid="add-offline-order-btn"
                onClick={() => setShowOfflineModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-terracotta text-primary-foreground text-xs font-medium hover:opacity-90 transition shadow-sm"
              >
                <Plus className="w-4 h-4" /> Add Offline Order
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  data-testid="orders-search-input"
                  placeholder="Search Name / Mobile / ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-muted border border-border pl-9 pr-8 py-2 rounded-md text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <select data-testid="orders-mode-filter" value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} className="bg-muted border border-border px-3 py-2 rounded-md text-sm">
                <option value="all">All Modes (Online &amp; Offline)</option>
                <option value="Online">Online Orders</option>
                <option value="Offline">Offline Orders</option>
              </select>
              <select data-testid="orders-range" value={range} onChange={(e) => setRange(e.target.value)} className="bg-muted border border-border px-3 py-2 rounded-md text-sm">
                {Object.entries(RANGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select data-testid="orders-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-muted border border-border px-3 py-2 rounded-md text-sm">
                <option value="all">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Direct Orders Summary Banner */}
          {filtered.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 bg-card border border-border p-4 rounded-xl shadow-xs mb-3">
              <div className="p-2.5 rounded-lg bg-muted/40 border border-border/80 text-center">
                <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider block mb-1">Total Revenue</span>
                <span className="text-lg font-bold font-price text-foreground">₹{directSummary.totalSales.toLocaleString("en-IN")}</span>
              </div>
              <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-300/40 text-center">
                <span className="text-[10px] uppercase font-bold text-amber-700 dark:text-amber-400 tracking-wider block mb-1">Purchase / Material Cost</span>
                <span className="text-lg font-bold font-price text-amber-700 dark:text-amber-400">₹{directSummary.totalPurchase.toLocaleString("en-IN")}</span>
              </div>
              <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-300/40 text-center">
                <span className="text-[10px] uppercase font-bold text-blue-700 dark:text-blue-400 tracking-wider block mb-1">Shipping Outflow</span>
                <span className="text-lg font-bold font-price text-blue-700 dark:text-blue-400">₹{directSummary.totalShipping.toLocaleString("en-IN")}</span>
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-300/40 text-center">
                <span className="text-[10px] uppercase font-bold text-emerald-800 dark:text-emerald-300 tracking-wider block mb-1">Calculated Tax</span>
                <span className="text-lg font-bold font-price text-emerald-800 dark:text-emerald-300">₹{directSummary.totalTax.toLocaleString("en-IN")}</span>
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-300/40 text-center col-span-2 sm:col-span-1">
                <span className="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-400 tracking-wider block mb-1">Net Order Profit</span>
                <span className={`text-lg font-bold font-price ${directSummary.totalProfit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700"}`}>
                  ₹{directSummary.totalProfit.toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}

          {filtered.length === 0 && <div className="border border-dashed border-border p-8 text-center text-muted-foreground">No orders match filter criteria.</div>}

          {filtered.map((o) => {
            const isOff = o.orderMode === "Offline";
            const oCost = orderPurchaseCost(o, products);
            const oShip = orderShipping(o, products);
            const oTax = orderTaxTotal(o);
            const oTotal = orderGrandTotal(o, products);
            const oProf = calculateSingleOrderProfit(o, products);

            const t = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : (o.createdAt ? new Date(o.createdAt).getTime() : null);
            const formattedDate = t && !isNaN(t)
              ? new Date(t).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
              : (o.date || "Date N/A");

            return (
              <details key={o.id} data-testid={`admin-order-${o.id}`} className="border border-border bg-card rounded-xl overflow-hidden shadow-xs">
                <summary className="p-4 flex flex-wrap items-center gap-3 cursor-pointer select-none hover:bg-muted/30 transition">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-medium text-foreground text-sm flex flex-wrap items-center gap-2">
                      <span>{o.customerName} — {o.phone}</span>
                      <span className="text-xs font-mono font-semibold bg-muted px-2 py-0.5 rounded text-foreground border border-border/60">
                        ID: {String(o.id || "").toUpperCase()}
                      </span>
                      {o.isNonGst || o.nonGst || o.isGstOrder === false || o.gstType === "non-gst" || o.gstMode === "none" ? (
                        <span className="text-[10px] font-bold bg-neutral-200 dark:bg-neutral-800 text-muted-foreground px-1.5 py-0.5 rounded border border-border">
                          Non-GST
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-500/20">
                          GST ({o.state ? (isWestBengalState(o.state) ? "WB" : "IGST") : "WB"})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-0.5">Purchased: {formattedDate}</div>
                  </div>

                  {/* Summary Metric Chips */}
                  <div className="flex flex-wrap items-center gap-1.5 text-xs font-mono">
                    <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-300/40" title="Purchase / Material Cost">
                      Purchase ₹{oCost.toLocaleString("en-IN")}
                    </span>
                    <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-300/40" title="Shipping Cost">
                      Ship ₹{oShip.toLocaleString("en-IN")}
                    </span>
                    {oTax > 0 && (
                      <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border border-emerald-300/50 font-bold" title="Calculated Tax">
                        Tax ₹{oTax.toLocaleString("en-IN")}
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded bg-muted text-foreground font-bold border border-border" title="Grand Total">
                      Total ₹{oTotal.toLocaleString("en-IN")}
                    </span>
                    <span className={`px-2 py-0.5 rounded font-bold border ${
                      oProf >= 0
                        ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-300/50"
                        : "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-300/50"
                    }`} title="Net Profit">
                      Profit ₹{oProf.toLocaleString("en-IN")}
                    </span>
                  </div>

                  <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_CONFIG[o.status]?.badge || "bg-muted text-foreground border-border"}`}>
                    {o.status}
                  </span>
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <a
                      data-testid={`print-${o.id}`}
                      href={`/admin/print/${o.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-foreground text-background hover:bg-terracotta transition cursor-pointer"
                      title="Open printable invoice + shipping label"
                    >
                      <Printer className="w-3 h-3" /> Invoice + Label
                    </a>
                    <button
                      type="button"
                      data-testid={`edit-items-summary-${o.id}`}
                      onClick={() => setEditingItemsOrder(o)}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta hover:bg-terracotta hover:text-white transition font-medium cursor-pointer"
                      title="Edit, Add or Remove product items from this order"
                    >
                      <Package className="w-3 h-3" /> Items
                    </button>
                    <button
                      type="button"
                      data-testid={`edit-customer-summary-${o.id}`}
                      onClick={() => setEditingCustomerOrder(o)}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-muted text-foreground hover:bg-terracotta hover:text-white transition font-medium cursor-pointer border border-border"
                      title="Edit customer and shipping details"
                    >
                      <UserCheck className="w-3 h-3" /> Customer
                    </button>
                    <button
                      type="button"
                      data-testid={`delete-order-summary-${o.id}`}
                      onClick={() => setDeletingOrder(o)}
                      className="p-1.5 rounded-lg bg-rose-500/10 text-rose-700 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition font-medium cursor-pointer border border-rose-500/20"
                      title="Delete this order"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </summary>
                <div className="p-4 border-t border-border grid md:grid-cols-2 gap-4 text-sm bg-background/50">
                  <div>
                    <div className="flex items-center justify-between mb-2 pb-1 border-b border-border">
                      <div className="overline text-muted-foreground font-semibold uppercase tracking-wider text-[10px]">
                        Items &amp; Tax Calculation ({o.items?.length || 0})
                      </div>
                      <button
                        type="button"
                        data-testid={`edit-items-${o.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingItemsOrder(o);
                        }}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg bg-terracotta/10 text-terracotta hover:bg-terracotta hover:text-white transition cursor-pointer"
                        title="Edit quantities, prices, taxes, add new products, or remove items"
                      >
                        <Edit3 className="w-3 h-3" /> Edit Items
                      </button>
                    </div>
                    <ul className="space-y-1.5">{o.items.map((i, k) => {
                      const iRate = Number(i.taxRate != null ? i.taxRate : (i.gstRate || 0));
                      const iPrice = Number(i.price || 0);
                      const iQty = Number(i.quantity || 1);
                      const iTaxAmt = Number(i.taxAmount != null && !isNaN(i.taxAmount) ? i.taxAmount : ((iPrice * iQty * iRate) / 100));
                      const iLineTotal = Number(i.total != null && !isNaN(i.total) ? i.total : ((iPrice * iQty) + iTaxAmt));

                      return (
                        <li key={k} className="text-xs flex flex-wrap items-center justify-between gap-2 p-1.5 rounded bg-muted/40 border border-border/60 hover:border-terracotta/30 transition">
                          <div className="flex items-center gap-1.5 flex-1 min-w-[150px]">
                            <span className="font-medium text-foreground">• {i.name}</span>
                            {i.variantTag && i.variantTag !== "Default" && <span className="text-muted-foreground"> [{i.variantTag}]</span>}
                            <span className="text-muted-foreground ml-1">× {iQty} @ ₹{iPrice}</span>
                          </div>
                          <div className="flex items-center gap-2 font-mono text-[11px]">
                            {iRate > 0 ? (
                              <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 font-semibold border border-emerald-500/20">
                                Tax ({iRate}%): ₹{Math.round(iTaxAmt)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-[10px]">Tax: 0%</span>
                            )}
                            <span className="font-bold text-foreground">
                              Total: ₹{Math.round(iLineTotal)}
                            </span>
                            {o.items.length > 1 && (
                              <button
                                type="button"
                                data-testid={`quick-delete-item-${o.id}-${k}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!window.confirm(`Delete "${i.name}" from order #${o.id}?`)) return;
                                  const updatedItems = o.items.filter((_, idx) => idx !== k);
                                  const newFin = recalculateOrderFinancials(o, updatedItems, products);
                                  await updateOrder(o.id, {
                                    items: updatedItems,
                                    ...newFin,
                                  });
                                  toast.success(`Removed "${i.name}" from order`);
                                  refresh();
                                }}
                                className="p-1 text-muted-foreground hover:text-rose-600 rounded transition cursor-pointer"
                                title="Delete this item"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}</ul>
                      <div className="mt-3 bg-card border border-border p-3.5 rounded-xl space-y-2.5">
                        <div className="flex items-center justify-between pb-2 border-b border-border">
                          <span className="overline text-terracotta font-semibold text-[11px]">Customer &amp; Shipping Information</span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid={`edit-customer-${o.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCustomerOrder(o);
                              }}
                              className="text-xs text-primary bg-primary/10 hover:bg-primary hover:text-primary-foreground px-2 py-0.5 rounded flex items-center gap-1 font-medium transition cursor-pointer"
                              title="Edit customer name, phone, email, address, state, GSTIN, notes"
                            >
                              <Edit3 className="w-3 h-3" /> Edit Details
                            </button>
                            <button
                              type="button"
                              data-testid={`copy-all-${o.id}`}
                              onClick={() => {
                                const fullInfo = `Order ID: ${o.id || ""}\nName: ${o.customerName || o.name || ""}\nPhone: ${o.phone || ""}\nAddress: ${o.address || ""}\nCity: ${o.city || ""}${o.state ? `, ${o.state}` : ""}\nPin Code: ${o.pincode || o.pin || ""}`;
                                navigator.clipboard.writeText(fullInfo);
                                toast.success("All shipping details copied!");
                              }}
                              className="text-xs text-terracotta hover:underline flex items-center gap-1 font-medium cursor-pointer"
                            >
                              <Copy className="w-3.5 h-3.5" /> Copy All Details
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          {/* 1. Order ID */}
                          <div className="flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded-lg border border-border/60">
                            <div className="min-w-0 flex-1 pr-1">
                              <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Order ID</span>
                              <span className="font-mono font-semibold text-foreground break-all block select-all">{String(o.id || "").toUpperCase()}</span>
                            </div>
                            <button
                              type="button"
                              data-testid={`copy-id-${o.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(String(o.id || "").toUpperCase());
                                toast.success("Full Order ID copied!");
                              }}
                              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition cursor-pointer shrink-0"
                              title="Copy Order ID"
                            >
                              <Copy className="w-3.5 h-3.5 text-terracotta" />
                            </button>
                          </div>

                          {/* 2. Customer Name */}
                          <div className="flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded-lg border border-border/60">
                            <div className="min-w-0 flex-1 pr-1">
                              <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Name</span>
                              <span className="font-medium text-foreground truncate block">{o.customerName || o.name || "N/A"}</span>
                            </div>
                            <button
                              type="button"
                              data-testid={`copy-name-${o.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(o.customerName || o.name || "");
                                toast.success("Name copied!");
                              }}
                              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition cursor-pointer shrink-0"
                              title="Copy Name"
                            >
                              <Copy className="w-3.5 h-3.5 text-terracotta" />
                            </button>
                          </div>

                          {/* 3. Phone / Number */}
                          <div className="flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded-lg border border-border/60">
                            <div className="min-w-0 flex-1 pr-1">
                              <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Number</span>
                              <span className="font-mono font-semibold text-foreground truncate block">{o.phone || "N/A"}</span>
                            </div>
                            <button
                              type="button"
                              data-testid={`copy-phone-${o.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(o.phone || "");
                                toast.success("Phone Number copied!");
                              }}
                              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition cursor-pointer shrink-0"
                              title="Copy Phone Number"
                            >
                              <Copy className="w-3.5 h-3.5 text-terracotta" />
                            </button>
                          </div>

                          {/* 4. City */}
                          <div className="flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded-lg border border-border/60">
                            <div className="min-w-0 flex-1 pr-1">
                              <span className="text-[10px] uppercase text-muted-foreground block font-semibold">City</span>
                              <span className="font-medium text-foreground truncate block">{o.city || "N/A"}{o.state ? `, ${o.state}` : ""}</span>
                            </div>
                            <button
                              type="button"
                              data-testid={`copy-city-${o.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(o.city || "");
                                toast.success("City copied!");
                              }}
                              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition cursor-pointer shrink-0"
                              title="Copy City"
                            >
                              <Copy className="w-3.5 h-3.5 text-terracotta" />
                            </button>
                          </div>

                          {/* 5. Pin Code */}
                          <div className="flex items-center justify-between bg-muted/40 px-2.5 py-1.5 rounded-lg border border-border/60">
                            <div className="min-w-0 flex-1 pr-1">
                              <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Pin Code</span>
                              <span className="font-mono font-semibold text-foreground truncate block">{o.pincode || o.pin || "N/A"}</span>
                            </div>
                            <button
                              type="button"
                              data-testid={`copy-pincode-${o.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(o.pincode || o.pin || "");
                                toast.success("Pin Code copied!");
                              }}
                              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition cursor-pointer shrink-0"
                              title="Copy Pin Code"
                            >
                              <Copy className="w-3.5 h-3.5 text-terracotta" />
                            </button>
                          </div>

                          {/* Order Mode & Payment */}
                          <div className="flex flex-col justify-between bg-muted/40 px-2.5 py-1.5 rounded-lg border border-border/60">
                            <div>
                              <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Payment Details</span>
                              <span className="font-semibold text-foreground text-xs block">
                                {o.paymentMethod || (o.orderMode === "Offline" ? "In-Store / Direct" : "Online")}
                              </span>
                              {Number(o.codCharge || 0) > 0 || o.paymentType === "COD" || o.paymentMethod?.includes("COD") ? (
                                <div className="mt-1 text-[11px] leading-tight space-y-0.5">
                                  <span className="text-emerald-700 dark:text-emerald-400 font-semibold block">
                                    Adv Paid: ₹{o.advancePaid != null ? o.advancePaid : 0}
                                  </span>
                                  <span className="text-amber-800 dark:text-amber-300 font-bold block">
                                    Collect on Delivery: ₹{o.codBalanceDue != null ? o.codBalanceDue : Math.max(0, orderGrandTotal(o, products) - Number(o.advancePaid || 0))}
                                  </span>
                                  {Number(o.codCharge || 0) > 0 && (
                                    <span className="text-[10px] text-muted-foreground block">
                                      COD Fee: ₹{o.codCharge}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium block mt-0.5">
                                  Prepaid / Standard
                                </span>
                              )}
                            </div>
                          </div>

                          {/* 6. Address */}
                          <div className="col-span-1 sm:col-span-2 flex items-start justify-between bg-muted/40 px-2.5 py-1.5 rounded-lg border border-border/60">
                            <div className="flex-1 pr-2">
                              <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Address</span>
                              <span className="text-foreground text-xs leading-snug block">{o.address || "N/A"}</span>
                              {o.notes && <div className="mt-1 italic text-[11px] text-muted-foreground">Note: {o.notes}</div>}
                            </div>
                            <button
                              type="button"
                              data-testid={`copy-address-${o.id}`}
                              onClick={() => {
                                navigator.clipboard.writeText(o.address || "");
                                toast.success("Address copied!");
                              }}
                              className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition cursor-pointer shrink-0 mt-0.5"
                              title="Copy Address"
                            >
                              <Copy className="w-3.5 h-3.5 text-terracotta" />
                            </button>
                          </div>
                        </div>
                      </div>
                  </div>
                  <div className="space-y-3">
                    {/* Financial Summary Breakdown Cards for this specific Order */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 bg-muted/40 border border-border p-2.5 rounded-lg text-center font-mono">
                      <div className="p-1 bg-amber-500/10 rounded border border-amber-300/30">
                        <span className="text-[9px] uppercase font-bold text-amber-700 dark:text-amber-400 block">Purchase</span>
                        <span className="text-xs font-bold text-amber-700 dark:text-amber-400">₹{oCost.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="p-1 bg-blue-500/10 rounded border border-blue-300/30">
                        <span className="text-[9px] uppercase font-bold text-blue-700 dark:text-blue-400 block">Shipping</span>
                        <span className="text-xs font-bold text-blue-700 dark:text-blue-400">₹{oShip.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="p-1 bg-emerald-500/10 rounded border border-emerald-300/30">
                        <span className="text-[9px] uppercase font-bold text-emerald-700 dark:text-emerald-400 block">Calculated Tax</span>
                        <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400">₹{oTax.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="p-1 bg-background rounded border border-border">
                        <span className="text-[9px] uppercase font-bold text-muted-foreground block">Grand Total</span>
                        <span className="text-xs font-bold text-foreground">₹{oTotal.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="p-1 bg-emerald-500/10 rounded border border-emerald-300/30">
                        <span className="text-[9px] uppercase font-bold text-emerald-700 dark:text-emerald-400 block">Profit</span>
                        <span className={`text-xs font-bold ${oProf >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700"}`}>
                          ₹{oProf.toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 bg-muted/20 border border-border p-3 rounded-lg">
                      <label className="col-span-2 flex items-center gap-2 py-1 px-1.5 rounded bg-background/60 border border-border/80 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid={`delivery-included-${o.id}`}
                          defaultChecked={o.deliveryIncluded !== false}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            const updatedOrder = { ...o, deliveryIncluded: checked };
                            const newTotal = orderGrandTotal(updatedOrder, products);
                            const newBalance = orderBalanceDue({ ...updatedOrder, total: newTotal }, products);
                            await updateOrder(o.id, {
                              deliveryIncluded: checked,
                              total: newTotal,
                              codBalanceDue: newBalance,
                            });
                            toast.success(checked ? "Delivery marked as Included (Free in Invoice)" : "Delivery marked as Excluded (Shipping added to Grand Total)");
                            refresh();
                          }}
                          className="w-4 h-4 accent-terracotta rounded cursor-pointer"
                        />
                        <span className="text-xs font-medium text-foreground">
                          Delivery Included (Free Shipping in Invoice)
                        </span>
                        {o.deliveryIncluded === false && (
                          <span className="text-[10px] text-amber-700 dark:text-amber-400 font-medium ml-auto">
                            (Shipping excluded — added to Grand Total)
                          </span>
                        )}
                      </label>

                      <label className="block">
                        <span className="text-xs mb-1 block text-muted-foreground font-medium">
                          Shipping Charge (₹) {o.deliveryIncluded === false && <span className="text-[10px] text-amber-600 font-normal">(Added to Grand Total)</span>}
                        </span>
                        <input
                          data-testid={`shipping-${o.id}`}
                          type="number"
                          min="0"
                          defaultValue={o.shippingCharge != null ? o.shippingCharge : ""}
                          placeholder="0"
                          onBlur={async (e) => {
                            const val = Math.max(0, Number(e.target.value || 0));
                            if (val !== Number(o.shippingCharge || 0)) {
                              const updatedOrder = { ...o, shippingCharge: val };
                              const newTotal = orderGrandTotal(updatedOrder, products);
                              const newBalance = orderBalanceDue({ ...updatedOrder, total: newTotal }, products);
                              await updateOrder(o.id, {
                                shippingCharge: val,
                                total: newTotal,
                                codBalanceDue: newBalance,
                              });
                              toast.success(val > 0 ? `Shipping charge ₹${val} set (Added to bill if delivery excluded)` : "Shipping charge set to ₹0");
                              refresh();
                            }
                          }}
                          className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono outline-none focus:border-terracotta"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs mb-1 block text-muted-foreground font-medium">Discount (₹)</span>
                        <input
                          data-testid={`discount-${o.id}`}
                          type="number"
                          min="0"
                          defaultValue={orderDiscount(o) || ""}
                          placeholder="0"
                          onBlur={async (e) => {
                            const val = Math.max(0, Number(e.target.value || 0));
                            if (val !== orderDiscount(o)) {
                              const updatedOrder = { ...o, discount: val };
                              const newTotal = orderGrandTotal(updatedOrder, products);
                              const newBalance = orderBalanceDue({ ...updatedOrder, total: newTotal }, products);
                              await updateOrder(o.id, {
                                discount: val,
                                total: newTotal,
                                codBalanceDue: newBalance,
                              });
                              toast.success(val > 0 ? `Discount ₹${val} applied` : "Discount removed");
                              refresh();
                            }
                          }}
                          className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono outline-none focus:border-terracotta"
                        />
                      </label>

                      <label className="block">
                        <span className="text-xs mb-1 block text-muted-foreground font-medium">COD Handling Fee (₹)</span>
                        <input
                          data-testid={`cod-fee-${o.id}`}
                          type="number"
                          min="0"
                          defaultValue={o.codCharge != null ? o.codCharge : ""}
                          placeholder="0"
                          onBlur={async (e) => {
                            const val = e.target.value === "" ? 0 : Math.max(0, Number(e.target.value || 0));
                            if (val !== Number(o.codCharge || 0)) {
                              const updatedOrder = { ...o, codCharge: val };
                              const newTotal = orderGrandTotal(updatedOrder, products);
                              const newBalance = orderBalanceDue({ ...updatedOrder, total: newTotal }, products);
                              await updateOrder(o.id, {
                                codCharge: val,
                                total: newTotal,
                                codBalanceDue: newBalance,
                                ...(val > 0 && (!o.paymentMethod || !o.paymentMethod.includes("COD"))
                                  ? { paymentMethod: "Cash on Delivery (COD)", paymentType: "COD" }
                                  : (val === 0 && o.paymentMethod?.includes("COD") ? { paymentMethod: "Prepaid / Direct", paymentType: "Prepaid" } : {}))
                              });
                              toast.success(val > 0 ? `COD charge ₹${val} set` : "COD charge removed");
                              refresh();
                            }
                          }}
                          className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono outline-none focus:border-terracotta"
                        />
                      </label>

                      {/* Manual Purchase Cost & Manual Profit Entry */}
                      <label className="block">
                        <span className="text-xs mb-1 block text-muted-foreground font-medium">Purchase / Material Cost (₹)</span>
                        <input
                          data-testid={`purchase-cost-${o.id}`}
                          type="number"
                          min="0"
                          defaultValue={o.purchaseCost != null ? o.purchaseCost : ""}
                          placeholder="e.g. 450"
                          onBlur={async (e) => {
                            const val = e.target.value === "" ? null : Math.max(0, Number(e.target.value || 0));
                            if (val !== o.purchaseCost) {
                              await updateOrder(o.id, { purchaseCost: val });
                              toast.success(val != null ? `Purchase cost set to ₹${val}` : "Cleared manual purchase cost");
                              refresh();
                            }
                          }}
                          className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono outline-none focus:border-terracotta"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs mb-1 block text-muted-foreground font-medium">Order Net Profit (₹)</span>
                        <input
                          data-testid={`profit-${o.id}`}
                          type="number"
                          defaultValue={o.profit != null ? o.profit : ""}
                          placeholder="Auto-calculated"
                          onBlur={async (e) => {
                            const val = e.target.value === "" ? null : Number(e.target.value);
                            if (val !== o.profit) {
                              await updateOrder(o.id, { profit: val });
                              toast.success(val != null ? `Profit set to ₹${val}` : "Cleared manual profit override");
                              refresh();
                            }
                          }}
                          className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono outline-none focus:border-terracotta font-bold text-emerald-700 dark:text-emerald-400"
                        />
                      </label>

                      <div className="col-span-2 sm:col-span-3 text-[11px] text-muted-foreground pt-1.5 border-t border-border/60 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span>Sub: ₹{orderSubtotal(o)}</span>
                          {orderTaxTotal(o) > 0 && (
                            <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                              + Tax: ₹{orderTaxTotal(o)}
                            </span>
                          )}
                          {orderShipping(o, products) > 0 ? (
                            <span>{o.deliveryIncluded === false ? `+ Ship: ₹${orderShipping(o, products)} (Added to Bill)` : `Ship: ₹${orderShipping(o, products)} (Free)`}</span>
                          ) : o.deliveryIncluded === false ? (
                            <span className="text-amber-700 dark:text-amber-400 italic">
                              Ship: Will be calculated later
                            </span>
                          ) : (
                            ""
                          )}
                          {orderDiscount(o) > 0 ? <span>− Disc: ₹{orderDiscount(o)}</span> : ""}
                          {orderCodCharge(o) > 0 ? (
                            <span className="font-semibold text-amber-700 dark:text-amber-400">
                              + COD: ₹{orderCodCharge(o)}
                            </span>
                          ) : ""}
                          {(orderCodCharge(o) > 0 || o.paymentType === "COD" || o.paymentMethod?.includes("COD")) && (
                            <span className="text-emerald-700 dark:text-emerald-400 font-semibold">
                              (Adv: ₹{orderAdvanceAmount(o, products)} • Collect on Delivery: ₹{orderBalanceDue(o, products)})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-foreground text-xs">Grand Total: ₹{orderGrandTotal(o, products)}</span>
                          <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${oProf >= 0 ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 text-rose-700"}`}>
                            Profit: ₹{oProf}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="block bg-muted/20 border border-border p-3 rounded-lg space-y-2">
                      <span className="text-xs font-semibold uppercase tracking-wider block text-muted-foreground">Order Status</span>
                      <div className="flex flex-wrap gap-1.5">
                        {STATUSES.map((s) => {
                          const isActive = o.status === s;
                          const cfg = STATUS_CONFIG[s] || {
                            activeBtn: "bg-foreground text-background font-semibold",
                            idleBtn: "bg-muted text-muted-foreground hover:bg-accent",
                          };
                          return (
                            <button
                              key={s}
                              type="button"
                              data-testid={`status-btn-${o.id}-${s}`}
                              onClick={async () => {
                                if (isActive) return;
                                const patch = { status: s };
                                if (s === "Shipped" && !o.dispatchDate) {
                                  patch.dispatchDate = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
                                }
                                await updateOrder(o.id, patch);
                                toast.success(`Status updated to '${s}'`);
                                refresh();
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all flex items-center gap-1 cursor-pointer ${
                                isActive ? cfg.activeBtn : cfg.idleBtn
                              }`}
                            >
                              {isActive && <Check className="w-3.5 h-3.5" />}
                              {s}
                            </button>
                          );
                        })}
                      </div>
                      <select
                        data-testid={`status-${o.id}`}
                        value={o.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          const patch = { status: newStatus };
                          if (newStatus === "Shipped" && !o.dispatchDate) {
                            patch.dispatchDate = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
                          }
                          await updateOrder(o.id, patch);
                          toast.success(`Status updated to '${newStatus}'`);
                          refresh();
                        }}
                        className="w-full mt-1 bg-background border border-border px-3 py-1.5 rounded-md text-xs font-medium"
                      >
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </div>

                    {/* Post Office Consignment ID field */}
                    <label className="block bg-muted/20 border border-border p-3 rounded-lg">
                      <span className="text-xs font-semibold text-terracotta uppercase tracking-wider block mb-1">
                        Post Office Consignment ID / Speed Post No.
                      </span>
                      <div className="flex gap-2">
                        <input
                          data-testid={`consignment-${o.id}`}
                          defaultValue={o.consignmentNumber || o.trackingNumber || ""}
                          placeholder="e.g. SP847291038IN"
                          onBlur={async (e) => {
                            const val = e.target.value.trim().toUpperCase();
                            if (val !== (o.consignmentNumber || o.trackingNumber || "")) {
                              const patch = { consignmentNumber: val, trackingNumber: val };
                              if (val && o.status === "New") {
                                patch.status = "Shipped";
                              }
                              await updateOrder(o.id, patch);
                              toast.success("Consignment ID saved!");
                              refresh();
                            }
                          }}
                          className="flex-1 bg-background border border-border px-3 py-2 rounded-md font-mono text-sm uppercase outline-none focus:border-terracotta"
                        />
                      </div>
                      <span className="text-[11px] text-muted-foreground mt-1 block">
                        Saved consignment ID automatically populates customer tracking and speed post links.
                      </span>
                    </label>

                    {/* Carrier Partner & Dispatch Branch */}
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <span className="text-xs mb-1 block text-muted-foreground">Carrier Partner *</span>
                        <select
                          data-testid={`courier-${o.id}`}
                          value={getCarrierType(o.courierName)}
                          onChange={async (e) => {
                            const val = e.target.value;
                            await updateOrder(o.id, { courierName: val });
                            toast.success(`Carrier partner set to ${val}`);
                            refresh();
                          }}
                          className="w-full bg-muted/50 border border-border px-2.5 py-1.5 rounded text-xs font-medium"
                        >
                          {CARRIER_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="text-xs mb-1 block text-muted-foreground">Booking Branch / Depot</span>
                        <input
                          data-testid={`dispatch-po-${o.id}`}
                          defaultValue={o.dispatchPostOffice || "Bolpur Sub-Post Office"}
                          placeholder="e.g. Bolpur Branch"
                          onBlur={async (e) => {
                            if (e.target.value !== (o.dispatchPostOffice || "Bolpur Sub-Post Office")) {
                              await updateOrder(o.id, { dispatchPostOffice: e.target.value });
                              toast.success("Booking office updated");
                              refresh();
                            }
                          }}
                          className="w-full bg-muted/50 border border-border px-2.5 py-1.5 rounded text-xs"
                        />
                      </label>
                    </div>

                    {(getCarrierType(o.courierName) === "Others" || getCarrierType(o.courierName) === "Other") && (
                      <label className="block mt-2">
                        <span className="text-xs mb-1 block text-muted-foreground">Custom Courier / Transport Name</span>
                        <input
                          defaultValue={o.courierCustomName || (o.courierName !== "Others" && o.courierName !== "Other" ? o.courierName : "") || ""}
                          placeholder="e.g. BlueDart, DTDC, Local Transport"
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            await updateOrder(o.id, { courierCustomName: val, courierName: val || "Others" });
                            toast.success("Custom courier updated");
                            refresh();
                          }}
                          className="w-full bg-muted/50 border border-border px-2.5 py-1.5 rounded text-xs"
                        />
                      </label>
                    )}

                    {/* Tracking Link Actions */}
                    <div className="pt-2 border-t border-border space-y-2">
                      <div className="text-xs font-medium text-foreground flex items-center justify-between">
                        <span>Our Custom Tracking Link:</span>
                        <a
                          href={`/track/${o.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-terracotta underline font-normal flex items-center gap-1"
                        >
                          View Live Tracking Page <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const link = `${window.location.origin}/track/${o.id}`;
                            navigator.clipboard.writeText(link);
                            toast.success("Tracking link copied!");
                          }}
                          className="flex-1 px-3 py-1.5 rounded border border-border text-xs hover:bg-muted font-medium transition flex items-center justify-center gap-1"
                        >
                          <Copy className="w-3 h-3 text-terracotta" /> Copy Tracking Link
                        </button>
                        {(o.consignmentNumber || o.trackingNumber) && (
                          <button
                            type="button"
                            onClick={() => {
                              const cons = o.consignmentNumber || o.trackingNumber;
                              const carrier = getCarrierType(o.courierName);
                              const url = getCarrierTrackingUrl(o.courierName, cons);
                              navigator.clipboard.writeText(cons);
                              toast.success(`${carrier} tracking ID copied!`);
                              if (url) {
                                window.open(url, "_blank");
                              }
                            }}
                            className="px-3 py-1.5 rounded bg-terracotta text-primary-foreground text-xs hover:opacity-90 font-medium transition flex items-center gap-1"
                            title={`Verify tracking on official ${getCarrierType(o.courierName)} portal`}
                          >
                            {getCarrierType(o.courierName) === "Shiprocket" ? "Shiprocket Portal" : (getCarrierType(o.courierName) === "India Post" ? "India Post Portal" : (o.courierCustomName ? `${o.courierCustomName} Link` : "Carrier Link"))} <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <label className="block">
                      <span className="text-sm mb-1 block text-muted-foreground">Invoice URL</span>
                      <input
                        data-testid={`invoice-${o.id}`}
                        defaultValue={o.invoiceUrl || ""}
                        placeholder="https://…"
                        onBlur={async (e) => { if (e.target.value !== (o.invoiceUrl || "")) { await updateOrder(o.id, { invoiceUrl: e.target.value }); toast.success("Invoice saved"); refresh(); } }}
                        className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md"
                      />
                    </label>

                    <div className="pt-2 border-t border-border flex items-center justify-between">
                      <span className="text-[11px] text-muted-foreground">Order Management</span>
                      <button
                        type="button"
                        data-testid={`delete-order-card-${o.id}`}
                        onClick={() => setDeletingOrder(o)}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-rose-500/10 text-rose-700 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition font-semibold cursor-pointer border border-rose-500/20"
                        title="Delete entire order permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete Entire Order
                      </button>
                    </div>
                  </div>
                </div>
              </details>
            );
          })}
        </div>
      ) : (
        /* Marketplace Orders / Settlements Section */
        <div className="space-y-6">
          {/* Top Marketplace KPI Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">Gross Sales</span>
              <div className="text-xl font-serif font-bold text-foreground font-price mt-1">₹{mpStats.gross.toLocaleString()}</div>
              <span className="text-[10px] text-muted-foreground">Marketplace Sales</span>
            </div>

            <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
              <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider block">Net Settled Payout</span>
              <div className="text-xl font-serif font-bold text-emerald-700 dark:text-emerald-400 font-price mt-1">₹{mpStats.netPayout.toLocaleString()}</div>
              <span className="text-[10px] text-muted-foreground">Bank Deposited</span>
            </div>

            <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
              <span className="text-[11px] font-bold text-rose-700 dark:text-rose-400 uppercase tracking-wider block">Fees &amp; Deductions</span>
              <div className="text-xl font-serif font-bold text-rose-700 dark:text-rose-400 font-price mt-1">₹{mpStats.deductions.toLocaleString()}</div>
              <span className="text-[10px] text-muted-foreground">Comm + Logistics + Tax</span>
            </div>

            <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
              <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider block">Material COGS</span>
              <div className="text-xl font-serif font-bold text-amber-800 dark:text-amber-300 font-price mt-1">₹{mpStats.cogs.toLocaleString()}</div>
              <span className="text-[10px] text-muted-foreground">Product Costs</span>
            </div>

            <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
              <span className="text-[11px] font-bold text-terracotta uppercase tracking-wider block">Net Profit</span>
              <div className="text-xl font-serif font-bold text-terracotta font-price mt-1">₹{mpStats.profit.toLocaleString()}</div>
              <span className="text-[10px] text-muted-foreground">Payout − COGS</span>
            </div>

            <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
              <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">Units Sold</span>
              <div className="text-xl font-serif font-bold text-foreground font-price mt-1">{mpStats.units} Pcs</div>
              <span className="text-[10px] text-muted-foreground">Marketplace Volume</span>
            </div>
          </div>

          {/* Controls & Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-card border border-border p-3 rounded-xl">
            <div className="text-xs font-medium text-muted-foreground">
              Showing <strong className="text-foreground">{filteredMarketplaceOrders.length}</strong> of {marketplaceOrders.length} settlements
            </div>

            <div className="flex flex-wrap gap-2">
              <select
                data-testid="mp-filter-platform"
                value={mpFilterMarketplace}
                onChange={(e) => setMpFilterMarketplace(e.target.value)}
                className="bg-muted border border-border px-3 py-1.5 rounded-lg text-xs font-medium"
              >
                <option value="all">All Marketplaces</option>
                <option value="Amazon">Amazon</option>
                <option value="Flipkart">Flipkart</option>
                <option value="Meesho">Meesho</option>
                <option value="Myntra">Myntra</option>
                <option value="JioMart">JioMart</option>
                <option value="Tata CLIQ">Tata CLIQ</option>
                <option value="Cred">Cred</option>
                <option value="Nykaa">Nykaa</option>
                <option value="Other">Other Custom</option>
              </select>

              <select
                data-testid="mp-filter-range"
                value={mpFilterRange}
                onChange={(e) => setMpFilterRange(e.target.value)}
                className="bg-muted border border-border px-3 py-1.5 rounded-lg text-xs font-medium"
              >
                {Object.entries(RANGES).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>

              <select
                data-testid="mp-filter-status"
                value={mpFilterStatus}
                onChange={(e) => setMpFilterStatus(e.target.value)}
                className="bg-muted border border-border px-3 py-1.5 rounded-lg text-xs font-medium"
              >
                <option value="all">All Statuses</option>
                <option value="Settled">Settled</option>
                <option value="Pending Settlement">Pending Settlement</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>
          </div>

          {/* List of Marketplace Settlement Entries */}
          {filteredMarketplaceOrders.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center text-muted-foreground rounded-2xl">
              <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40 text-amber-600" />
              <div className="text-sm font-semibold text-foreground mb-1">No Marketplace Orders Logged</div>
              <p className="text-xs text-muted-foreground max-w-md mx-auto mb-4">
                Record weekly, monthly, or date-range order details and payouts received from Amazon, Flipkart, Meesho, Myntra, etc.
              </p>
              <button
                type="button"
                data-testid="add-first-mp-btn"
                onClick={() => {
                  setEditingMarketplaceOrder(null);
                  setShowMarketplaceModal(true);
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-terracotta text-primary-foreground text-xs font-medium hover:opacity-90 transition shadow-sm"
              >
                <Plus className="w-4 h-4" /> Add Marketplace Order Details
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMarketplaceOrders.map((m) => {
                const totalDeductions =
                  Number(m.commissionFee || 0) +
                  Number(m.logisticsFee || 0) +
                  Number(m.taxDeductions || 0) +
                  Number(m.otherDeductions || 0);

                return (
                  <details
                    key={m.id}
                    data-testid={`mp-order-${m.id}`}
                    className="border border-border bg-card rounded-xl overflow-hidden shadow-2xs group"
                  >
                    <summary className="p-4 flex flex-wrap items-center justify-between gap-4 cursor-pointer hover:bg-muted/30 transition">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 font-bold text-xs flex items-center justify-center border border-amber-500/20">
                          {m.marketplace.slice(0, 2).toUpperCase()}
                        </span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-foreground">
                              {m.marketplace === "Other" && m.marketplaceCustomName ? m.marketplaceCustomName : m.marketplace}
                            </span>
                            <span className="text-xs font-mono text-muted-foreground px-2 py-0.5 rounded bg-muted">
                              {m.settlementRef || "No Ref"}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Period: {m.startDate} to {m.endDate} | Payout Date: {m.payoutDate}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">Gross Sales / Net Payout</div>
                          <div className="text-sm font-bold font-price text-foreground">
                            ₹{m.grossSales?.toLocaleString()} → <span className="text-emerald-600 dark:text-emerald-400 font-extrabold">₹{m.netPayout?.toLocaleString()}</span>
                          </div>
                        </div>

                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-medium border ${
                            m.status === "Settled"
                              ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20"
                              : m.status === "Pending Settlement"
                              ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20"
                              : "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/20"
                          }`}
                        >
                          {m.status}
                        </span>

                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            data-testid={`edit-mp-${m.id}`}
                            onClick={() => {
                              setEditingMarketplaceOrder(m);
                            }}
                            className="p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground text-xs flex items-center gap-1 font-medium"
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            type="button"
                            data-testid={`delete-mp-${m.id}`}
                            onClick={async () => {
                              if (confirm(`Delete marketplace settlement entry "${m.settlementRef || m.marketplace}"?`)) {
                                await deleteMarketplaceOrder(m.id);
                                toast.success("Marketplace order entry deleted");
                                refresh();
                              }
                            }}
                            className="p-1.5 rounded-lg border border-border hover:bg-rose-500/10 text-muted-foreground hover:text-rose-600 text-xs"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </summary>

                    {/* Detailed Settlement Breakdown inside Details */}
                    <div className="p-4 border-t border-border bg-muted/20 text-xs space-y-4">
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <span className="text-muted-foreground block font-medium uppercase tracking-wider text-[10px]">
                            Items &amp; Quantity Sold
                          </span>
                          <div className="font-semibold text-foreground text-sm">
                            {m.unitsSold ? `${m.unitsSold} Units / Pcs Sold` : "Quantity not specified"}
                          </div>
                          {m.itemsSummary && (
                            <div className="text-muted-foreground mt-1 text-xs">
                              {m.itemsSummary}
                            </div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <span className="text-muted-foreground block font-medium uppercase tracking-wider text-[10px]">
                            Bank Account &amp; UTR Ref
                          </span>
                          <div className="font-medium text-foreground">
                            Bank Account: <strong>{m.paymentAccount || "Default Bank"}</strong>
                          </div>
                          {m.utrNumber && (
                            <div className="font-mono text-muted-foreground">
                              UTR / Txn: {m.utrNumber}
                            </div>
                          )}
                          {m.notes && <div className="italic text-muted-foreground mt-1">Note: {m.notes}</div>}
                        </div>

                        <div className="space-y-1 text-right sm:text-left">
                          <span className="text-muted-foreground block font-medium uppercase tracking-wider text-[10px]">
                            Financial Summary
                          </span>
                          <div className="text-terracotta font-bold text-sm font-price">
                            Net Profit: ₹{m.netProfit?.toLocaleString()}
                          </div>
                          <div className="text-muted-foreground">
                            Cost of Goods (COGS): ₹{m.cogs?.toLocaleString()}
                          </div>
                        </div>
                      </div>

                      {/* Itemised Deductions Table */}
                      <div className="bg-background border border-border p-3 rounded-lg">
                        <div className="font-bold text-muted-foreground text-[11px] uppercase tracking-wider mb-2">
                          Marketplace Deductions Breakdown
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-price text-xs text-center">
                          <div className="bg-muted/40 p-2 rounded">
                            <span className="text-[10px] text-muted-foreground block">Commission Fee</span>
                            <span className="font-semibold text-rose-600">₹{m.commissionFee || 0}</span>
                          </div>
                          <div className="bg-muted/40 p-2 rounded">
                            <span className="text-[10px] text-muted-foreground block">Logistics / Courier</span>
                            <span className="font-semibold text-rose-600">₹{m.logisticsFee || 0}</span>
                          </div>
                          <div className="bg-muted/40 p-2 rounded">
                            <span className="text-[10px] text-muted-foreground block">TCS / TDS Tax</span>
                            <span className="font-semibold text-rose-600">₹{m.taxDeductions || 0}</span>
                          </div>
                          <div className="bg-muted/40 p-2 rounded">
                            <span className="text-[10px] text-muted-foreground block">Ads / Other</span>
                            <span className="font-semibold text-rose-600">₹{m.otherDeductions || 0}</span>
                          </div>
                          <div className="bg-rose-500/10 p-2 rounded border border-rose-500/20 col-span-2 sm:col-span-1">
                            <span className="text-[10px] text-rose-700 dark:text-rose-300 block font-bold">Total Deductions</span>
                            <span className="font-bold text-rose-700 dark:text-rose-300">₹{totalDeductions.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Offline Order Modal */}
      {showOfflineModal && (
        <OfflineOrderModal
          products={products}
          masterSkus={masterSkus}
          onClose={() => setShowOfflineModal(false)}
          onSaved={() => {
            setShowOfflineModal(false);
            refresh();
          }}
        />
      )}

      {/* Marketplace Order Modal */}
      {(showMarketplaceModal || editingMarketplaceOrder) && (
        <MarketplaceOrderModal
          initialOrder={editingMarketplaceOrder}
          products={products}
          onClose={() => {
            setShowMarketplaceModal(false);
            setEditingMarketplaceOrder(null);
          }}
          onSaved={() => {
            setShowMarketplaceModal(false);
            setEditingMarketplaceOrder(null);
            refresh();
          }}
        />
      )}

      {/* Edit Order Items Modal (Product edit/delete/add with recalculations) */}
      {editingItemsOrder && (
        <EditOrderItemsModal
          order={editingItemsOrder}
          products={products}
          masterSkus={masterSkus}
          onClose={() => setEditingItemsOrder(null)}
          onSaved={() => {
            setEditingItemsOrder(null);
            refresh();
          }}
        />
      )}

      {/* Edit Customer Details Modal (Customer details, shipping address, state, GSTIN, notes) */}
      {editingCustomerOrder && (
        <EditCustomerModal
          order={editingCustomerOrder}
          products={products}
          onClose={() => setEditingCustomerOrder(null)}
          onSaved={() => {
            setEditingCustomerOrder(null);
            refresh();
          }}
        />
      )}

      {/* Delete Order Confirmation Dialog */}
      {deletingOrder && (
        <DeleteOrderConfirmModal
          order={deletingOrder}
          onClose={() => setDeletingOrder(null)}
          onDeleted={() => {
            setDeletingOrder(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// FINANCES — Costing, Expenses, Investments & Withdrawals Panel
// ============================================================================
function FinancesPanel({ orders = [], products = [], expenses = [], investments = [], withdrawals = [], openingBalances = {}, losses = [], refresh }) {
  const [finSubTab, setFinSubTab] = useState("costing"); // "costing" | "losses" | "openingBalances" | "expenses" | "investments" | "withdrawals"

  // Forms state
  const [expenseForm, setExpenseForm] = useState({
    description: "", amount: "", vendor: "", category: "Raw Materials", date: new Date().toISOString().slice(0, 10), paymentMethod: "UPI", notes: ""
  });
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState("All");

  const [investmentForm, setInvestmentForm] = useState({
    amount: "", investedBy: "Shuvasis", date: new Date().toISOString().slice(0, 10), notes: ""
  });
  const [showAddInvestment, setShowAddInvestment] = useState(false);

  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: "", withdrawnBy: "Shuvasis", date: new Date().toISOString().slice(0, 10), notes: ""
  });
  const [showAddWithdrawal, setShowAddWithdrawal] = useState(false);

  // Editable Product Costs
  const [editingProdCostId, setEditingProdCostId] = useState(null);
  const [editProductCost, setEditProductCost] = useState("");
  const [editPackagingCost, setEditPackagingCost] = useState("");
  const [editExtraCharge, setEditExtraCharge] = useState("");

  const handleSaveProductCosts = async (p) => {
    await saveProduct({
      ...p,
      productCost: Number(editProductCost || 0),
      packagingCost: Number(editPackagingCost || 0),
      extraCharge: Number(editExtraCharge || 0),
      costPrice: Number(editProductCost || 0),
    });
    toast.success(`Updated cost breakdown for ${p.name}`);
    setEditingProdCostId(null);
    refresh();
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.description || !expenseForm.amount) {
      return toast.error("Please provide expense description and amount");
    }
    await saveExpense(expenseForm);
    toast.success("Expense logged!");
    setExpenseForm({ description: "", amount: "", vendor: "", category: "Raw Materials", date: new Date().toISOString().slice(0, 10), paymentMethod: "UPI", notes: "" });
    setShowAddExpense(false);
    refresh();
  };

  const handleDeleteExpense = async (id) => {
    await deleteExpense(id);
    toast.success("Expense deleted");
    refresh();
  };

  const handleAddInvestment = async (e) => {
    e.preventDefault();
    if (!investmentForm.amount) return toast.error("Please enter investment amount");
    await saveInvestment(investmentForm);
    toast.success("Capital investment logged!");
    setInvestmentForm({ amount: "", investedBy: "Shuvasis", date: new Date().toISOString().slice(0, 10), notes: "" });
    setShowAddInvestment(false);
    refresh();
  };

  const handleDeleteInvestment = async (id) => {
    await deleteInvestment(id);
    toast.success("Investment record deleted");
    refresh();
  };

  const handleAddWithdrawal = async (e) => {
    e.preventDefault();
    if (!withdrawalForm.amount) return toast.error("Please enter withdrawal amount");
    await saveWithdrawal(withdrawalForm);
    toast.success("Partner payout recorded!");
    setWithdrawalForm({ amount: "", withdrawnBy: "Shuvasis", date: new Date().toISOString().slice(0, 10), notes: "" });
    setShowAddWithdrawal(false);
    refresh();
  };

  const handleDeleteWithdrawal = async (id) => {
    await deleteWithdrawal(id);
    toast.success("Withdrawal record deleted");
    refresh();
  };

  const filteredExpenses = useMemo(() => {
    if (expenseCategoryFilter === "All") return expenses;
    return expenses.filter((e) => e.category === expenseCategoryFilter);
  }, [expenses, expenseCategoryFilter]);

  const expenseCategories = ["All", "Raw Materials", "Hardware & Accessories", "Artisan Wages", "Packaging", "Logistics & Courier", "Marketing", "Office & Tools", "Miscellaneous"];

  return (
    <div className="space-y-8 animate-fade-up">
      {/* Sub-Navigation Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h2 className="font-serif text-2xl font-bold">Studio Costing &amp; Accounting</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Manage fixed product costs, operational expenses, capital investments, and partner draws.</p>
        </div>
        <div className="flex items-center gap-2 bg-muted p-1 rounded-full border border-border">
          <button
            onClick={() => setFinSubTab("costing")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${finSubTab === "costing" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
          >
            Product Costing &amp; Margins
          </button>
          <button
            data-testid="subtab-losses"
            onClick={() => setFinSubTab("losses")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${finSubTab === "losses" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
          >
            Losses &amp; Damage ({losses.length})
          </button>
          <button
            data-testid="subtab-opening-balances"
            onClick={() => setFinSubTab("openingBalances")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${finSubTab === "openingBalances" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
          >
            Initial Balances (Past 1-Year Baseline)
          </button>
          <button
            onClick={() => setFinSubTab("expenses")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${finSubTab === "expenses" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
          >
            Expenses ({expenses.length})
          </button>
          <button
            onClick={() => setFinSubTab("investments")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${finSubTab === "investments" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
          >
            Capital Investments ({investments.length})
          </button>
          <button
            onClick={() => setFinSubTab("withdrawals")}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${finSubTab === "withdrawals" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}
          >
            Withdrawals ({withdrawals.length})
          </button>
        </div>
      </div>

      {/* SECTION: OPENING BALANCES / PAST 1-YEAR BASELINE */}
      {finSubTab === "openingBalances" && (
        <OpeningBalancesSubPanel openingBalances={openingBalances} refresh={refresh} />
      )}

      {/* SECTION: LOSSES & DAMAGE TRACKER */}
      {finSubTab === "losses" && (
        <LossesSubPanel losses={losses} openingLosses={openingBalances.openingLosses} refresh={refresh} />
      )}

      {/* SECTION 1: COSTING & PRODUCT MARGIN MATRIX */}
      {finSubTab === "costing" && (
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-serif text-xl font-bold flex items-center gap-2">
                <Calculator className="w-5 h-5 text-terracotta" /> Fixed Product &amp; Packaging Costing
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Formula: Product Cost (fixed) + Packaging Cost (fixed) + Extra Charge (dynamic) = Total Unit COGS.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">
                  <th className="py-3 px-4 font-semibold">Product</th>
                  <th className="py-3 px-4 font-semibold">Selling Price</th>
                  <th className="py-3 px-4 font-semibold">Fixed Product Cost</th>
                  <th className="py-3 px-4 font-semibold">Fixed Packaging</th>
                  <th className="py-3 px-4 font-semibold">Extra Charge</th>
                  <th className="py-3 px-4 font-semibold">Total Unit COGS</th>
                  <th className="py-3 px-4 font-semibold">Net Unit Margin</th>
                  <th className="py-3 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {products.map((p) => {
                  const retailPrice = Number(p.price || 0);
                  const isEditing = editingProdCostId === p.id;

                  const prodCost = isEditing ? Number(editProductCost || 0) : Number(p.productCost ?? p.costPrice ?? Math.round(retailPrice * 0.45));
                  const packCost = isEditing ? Number(editPackagingCost || 0) : Number(p.packagingCost ?? 80);
                  const extraChg = isEditing ? Number(editExtraCharge || 0) : Number(p.extraCharge ?? 0);

                  const totalUnitCogs = prodCost + packCost + extraChg;
                  const unitProfit = retailPrice - totalUnitCogs;
                  const marginPct = retailPrice > 0 ? ((unitProfit / retailPrice) * 100).toFixed(1) : 0;

                  return (
                    <tr key={p.id} className="hover:bg-muted/30 transition">
                      <td className="py-3.5 px-4 font-medium flex items-center gap-3">
                        <img
                          src={normaliseImageUrl(primaryImage(p))}
                          alt={p.name}
                          onError={handleImageError}
                          referrerPolicy="no-referrer"
                          className="w-10 h-10 object-cover rounded-lg border border-border shrink-0"
                        />
                        <div>
                          <div>{p.name}</div>
                          <div className="text-[11px] text-muted-foreground">{p.category}</div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 font-mono font-semibold">₹{retailPrice}</td>

                      <td className="py-3.5 px-4 font-mono">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editProductCost}
                            onChange={(e) => setEditProductCost(e.target.value)}
                            className="w-20 px-2 py-1 bg-background border border-terracotta rounded text-xs outline-none"
                            placeholder="Cost ₹"
                          />
                        ) : (
                          <span className="text-foreground">₹{prodCost}</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 font-mono">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editPackagingCost}
                            onChange={(e) => setEditPackagingCost(e.target.value)}
                            className="w-20 px-2 py-1 bg-background border border-terracotta rounded text-xs outline-none"
                            placeholder="Pack ₹"
                          />
                        ) : (
                          <span className="text-foreground">₹{packCost}</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 font-mono">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editExtraCharge}
                            onChange={(e) => setEditExtraCharge(e.target.value)}
                            className="w-20 px-2 py-1 bg-background border border-terracotta rounded text-xs outline-none"
                            placeholder="Extra ₹"
                          />
                        ) : (
                          <span className="text-foreground">₹{extraChg}</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 font-mono font-bold text-amber-700 dark:text-amber-400">
                        ₹{totalUnitCogs}
                      </td>

                      <td className="py-3.5 px-4 font-mono font-semibold">
                        <span className="text-emerald-700 dark:text-emerald-400 mr-2">+₹{unitProfit}</span>
                        <span className="bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded text-xs">
                          {marginPct}%
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleSaveProductCosts(p)}
                              className="px-3 py-1 bg-terracotta text-primary-foreground rounded text-xs font-medium hover:opacity-90"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingProdCostId(null)}
                              className="px-2 py-1 border border-border rounded text-xs hover:bg-muted"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingProdCostId(p.id);
                              setEditProductCost(p.productCost ?? p.costPrice ?? Math.round(retailPrice * 0.45));
                              setEditPackagingCost(p.packagingCost ?? 80);
                              setEditExtraCharge(p.extraCharge ?? 0);
                            }}
                            className="text-xs text-terracotta hover:underline font-medium inline-flex items-center gap-1"
                          >
                            <Edit3 className="w-3.5 h-3.5" /> Edit Costs
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SECTION 2: EXPENSES TRACKER */}
      {finSubTab === "expenses" && (
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl font-bold flex items-center gap-2">
                <Receipt className="w-5 h-5 text-terracotta" /> Operational Expenses Management
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">Record raw materials, vendor payouts, utility, and studio maintenance.</p>
            </div>
            <button
              onClick={() => setShowAddExpense(!showAddExpense)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-primary-foreground rounded-full text-xs font-medium hover:opacity-90 transition"
            >
              <Plus className="w-4 h-4" /> {showAddExpense ? "Close Form" : "Log New Expense"}
            </button>
          </div>

          {showAddExpense && (
            <form onSubmit={handleAddExpense} className="bg-muted/40 border border-border rounded-xl p-5 space-y-4 animate-fade-down">
              <h4 className="font-serif text-lg font-semibold">Add Expense Entry</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Date *</label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Description / Item *</label>
                  <input
                    type="text"
                    placeholder="e.g. Full Grain Leather Hides"
                    value={expenseForm.description}
                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Amount (₹) *</label>
                  <input
                    type="number"
                    placeholder="e.g. 14500"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Vendor / Payee</label>
                  <input
                    type="text"
                    placeholder="e.g. Kolkata Tanneries / India Post"
                    value={expenseForm.vendor}
                    onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                  >
                    {expenseCategories.filter((c) => c !== "All").map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Payment Method</label>
                  <select
                    value={expenseForm.paymentMethod}
                    onChange={(e) => setExpenseForm({ ...expenseForm, paymentMethod: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                  >
                    <option value="UPI">UPI / GooglePay</option>
                    <option value="Bank Transfer">Bank Transfer / NEFT</option>
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Notes (Optional)</label>
                <input
                  type="text"
                  placeholder="Additional notes"
                  value={expenseForm.notes}
                  onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddExpense(false)}
                  className="px-4 py-2 border border-border rounded-full text-xs hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-terracotta text-primary-foreground font-medium rounded-full text-xs hover:opacity-90"
                >
                  Save Expense
                </button>
              </div>
            </form>
          )}

          {/* Expenses Filter */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 text-xs">
            <span className="text-muted-foreground font-medium shrink-0 flex items-center gap-1">
              <Filter className="w-3.5 h-3.5" /> Category:
            </span>
            {expenseCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setExpenseCategoryFilter(cat)}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition ${
                  expenseCategoryFilter === cat ? "bg-terracotta text-primary-foreground font-medium" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">
                  <th className="py-3 px-4 font-semibold">Date</th>
                  <th className="py-3 px-4 font-semibold">Description</th>
                  <th className="py-3 px-4 font-semibold">Vendor</th>
                  <th className="py-3 px-4 font-semibold">Category</th>
                  <th className="py-3 px-4 font-semibold">Payment</th>
                  <th className="py-3 px-4 font-semibold">Amount</th>
                  <th className="py-3 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredExpenses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-muted-foreground text-xs">No expenses recorded under this category.</td>
                  </tr>
                ) : (
                  filteredExpenses.map((ex) => (
                    <tr key={ex.id} className="hover:bg-muted/30 transition">
                      <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{ex.date}</td>
                      <td className="py-3 px-4 font-medium">{ex.description || ex.title}</td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">{ex.vendor || "—"}</td>
                      <td className="py-3 px-4"><span className="bg-muted px-2.5 py-0.5 rounded-full text-[11px] font-medium">{ex.category}</span></td>
                      <td className="py-3 px-4 text-xs font-mono text-muted-foreground">{ex.paymentMethod}</td>
                      <td className="py-3 px-4 font-mono font-bold text-rose-700 dark:text-rose-400">₹{Number(ex.amount).toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => handleDeleteExpense(ex.id)} className="p-1 hover:text-rose-600">
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-rose-600" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {filteredExpenses.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold bg-muted/20">
                    <td colSpan={5} className="py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">
                      Total ({expenseCategoryFilter === "All" ? "All Categories" : expenseCategoryFilter})
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-rose-700 dark:text-rose-400">
                      ₹{filteredExpenses.reduce((s, e) => s + Number(e.amount || 0), 0).toLocaleString("en-IN")}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* SECTION 3: INVESTMENTS TRACKER */}
      {finSubTab === "investments" && (
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl font-bold flex items-center gap-2">
                <Wallet className="w-5 h-5 text-terracotta" /> Partner Investments &amp; Capital Injections
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">Track equity capital contributions by partners Shuvasis and Sourav.</p>
            </div>
            <button
              onClick={() => setShowAddInvestment(!showAddInvestment)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-primary-foreground rounded-full text-xs font-medium hover:opacity-90 transition"
            >
              <Plus className="w-4 h-4" /> {showAddInvestment ? "Close Form" : "Log Capital Contribution"}
            </button>
          </div>

          {showAddInvestment && (
            <form onSubmit={handleAddInvestment} className="bg-muted/40 border border-border rounded-xl p-5 space-y-4 animate-fade-down">
              <h4 className="font-serif text-lg font-semibold">New Capital Investment Entry</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Date *</label>
                  <input
                    type="date"
                    value={investmentForm.date}
                    onChange={(e) => setInvestmentForm({ ...investmentForm, date: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Amount (₹) *</label>
                  <input
                    type="number"
                    placeholder="e.g. 50000"
                    value={investmentForm.amount}
                    onChange={(e) => setInvestmentForm({ ...investmentForm, amount: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Invested By *</label>
                  <select
                    value={investmentForm.investedBy}
                    onChange={(e) => setInvestmentForm({ ...investmentForm, investedBy: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                  >
                    <option value="Shuvasis">Shuvasis</option>
                    <option value="Sourav">Sourav</option>
                    <option value="External Partner">External Partner</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Notes / Purpose</label>
                <input
                  type="text"
                  placeholder="e.g. Leather batch procurement capital / Equipment purchase"
                  value={investmentForm.notes}
                  onChange={(e) => setInvestmentForm({ ...investmentForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddInvestment(false)}
                  className="px-4 py-2 border border-border rounded-full text-xs hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-terracotta text-primary-foreground font-medium rounded-full text-xs hover:opacity-90"
                >
                  Record Investment
                </button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">
                  <th className="py-3 px-4 font-semibold">Date</th>
                  <th className="py-3 px-4 font-semibold">Invested By</th>
                  <th className="py-3 px-4 font-semibold">Amount</th>
                  <th className="py-3 px-4 font-semibold">Notes / Details</th>
                  <th className="py-3 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {investments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground text-xs">No investments recorded yet.</td>
                  </tr>
                ) : (
                  investments.map((inv) => (
                    <tr key={inv.id} className="hover:bg-muted/30 transition">
                      <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{inv.date}</td>
                      <td className="py-3 px-4 font-medium">
                        <span className="bg-amber-100 text-amber-900 border border-amber-300 dark:bg-amber-950/60 dark:text-amber-200 px-3 py-1 rounded-full text-xs font-semibold">
                          {inv.investedBy}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-emerald-700 dark:text-emerald-400">₹{Number(inv.amount).toLocaleString()}</td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">{inv.notes || "Studio capital contribution"}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => handleDeleteInvestment(inv.id)} className="p-1 hover:text-rose-600">
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-rose-600" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SECTION 4: WITHDRAWALS TRACKING */}
      {finSubTab === "withdrawals" && (
        <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-serif text-xl font-bold flex items-center gap-2">
                <ArrowDownRight className="w-5 h-5 text-terracotta" /> Partner Withdrawals &amp; Profit Draws
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">Track partner payouts, dividend draws, and personal withdrawals by Shuvasis and Sourav.</p>
            </div>
            <button
              onClick={() => setShowAddWithdrawal(!showAddWithdrawal)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-primary-foreground rounded-full text-xs font-medium hover:opacity-90 transition"
            >
              <Plus className="w-4 h-4" /> {showAddWithdrawal ? "Close Form" : "Record Partner Draw"}
            </button>
          </div>

          {showAddWithdrawal && (
            <form onSubmit={handleAddWithdrawal} className="bg-muted/40 border border-border rounded-xl p-5 space-y-4 animate-fade-down">
              <h4 className="font-serif text-lg font-semibold">Record Partner Withdrawal Entry</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Date *</label>
                  <input
                    type="date"
                    value={withdrawalForm.date}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, date: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Amount (₹) *</label>
                  <input
                    type="number"
                    placeholder="e.g. 15000"
                    value={withdrawalForm.amount}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, amount: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Withdrawn By *</label>
                  <select
                    value={withdrawalForm.withdrawnBy}
                    onChange={(e) => setWithdrawalForm({ ...withdrawalForm, withdrawnBy: e.target.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                  >
                    <option value="Shuvasis">Shuvasis</option>
                    <option value="Sourav">Sourav</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Notes / Purpose</label>
                <input
                  type="text"
                  placeholder="e.g. Monthly profit payout draw"
                  value={withdrawalForm.notes}
                  onChange={(e) => setWithdrawalForm({ ...withdrawalForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs outline-none focus:border-terracotta"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddWithdrawal(false)}
                  className="px-4 py-2 border border-border rounded-full text-xs hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-terracotta text-primary-foreground font-medium rounded-full text-xs hover:opacity-90"
                >
                  Record Withdrawal
                </button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground bg-muted/40">
                  <th className="py-3 px-4 font-semibold">Date</th>
                  <th className="py-3 px-4 font-semibold">Withdrawn By</th>
                  <th className="py-3 px-4 font-semibold">Amount</th>
                  <th className="py-3 px-4 font-semibold">Notes / Details</th>
                  <th className="py-3 px-4 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {withdrawals.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground text-xs">No partner withdrawals logged yet.</td>
                  </tr>
                ) : (
                  withdrawals.map((wd) => (
                    <tr key={wd.id} className="hover:bg-muted/30 transition">
                      <td className="py-3 px-4 font-mono text-xs text-muted-foreground">{wd.date}</td>
                      <td className="py-3 px-4 font-medium">
                        <span className="bg-rose-100 text-rose-900 border border-rose-300 dark:bg-rose-950/60 dark:text-rose-200 px-3 py-1 rounded-full text-xs font-semibold">
                          {wd.withdrawnBy}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-rose-700 dark:text-rose-400">₹{Number(wd.amount).toLocaleString()}</td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">{wd.notes || "Partner draw"}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => handleDeleteWithdrawal(wd.id)} className="p-1 hover:text-rose-600">
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-rose-600" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// OPENING BALANCES / INITIAL HISTORICAL FIGURES PANEL
// ============================================================================
function OpeningBalancesSubPanel({ openingBalances = {}, refresh }) {
  const [form, setForm] = useState(openingBalances);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(openingBalances || {});
  }, [openingBalances]);

  const handleChange = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const numericForm = {
        periodLabel: form.periodLabel || "Opening Balances / Initial Data",
        openingRevenue: Number(form.openingRevenue || 0),
        openingOrdersCount: Number(form.openingOrdersCount || 0),
        openingQuantitySold: Number(form.openingQuantitySold || 0),
        openingCOGS: Number(form.openingCOGS || 0),
        openingShipping: Number(form.openingShipping || 0),
        openingExpenses: Number(form.openingExpenses || 0),
        openingLosses: Number(form.openingLosses || 0),
        openingProfit: Number(form.openingProfit || 0),
        openingCapital: Number(form.openingCapital || 0),
        openingCapitalShuvasis: Number(form.openingCapitalShuvasis || 0),
        openingCapitalSourav: Number(form.openingCapitalSourav || 0),
        openingWithdrawals: Number(form.openingWithdrawals || 0),
        openingWithdrawalsShuvasis: Number(form.openingWithdrawalsShuvasis || 0),
        openingWithdrawalsSourav: Number(form.openingWithdrawalsSourav || 0),
        openingCashOnHand: Number(form.openingCashOnHand || 0),
        notes: form.notes || "",
      };
      await saveOpeningBalances(numericForm);
      toast.success("Opening balances saved! Dashboard financial summary updated.");
      refresh();
    } catch (err) {
      toast.error(err.message || "Failed to save opening balances");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h3 className="font-serif text-xl font-bold flex items-center gap-2">
            <History className="w-5 h-5 text-terracotta" /> Initial Business Figures &amp; Opening Balances
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Enter lump-sum starting figures from your past 1 year of operations. These baseline numbers automatically combine with live transactions.
          </p>
        </div>
        <span className="bg-terracotta/10 text-terracotta border border-terracotta/20 px-3 py-1 rounded-full text-xs font-semibold">
          1-Year Baseline Setup
        </span>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Baseline Meta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Baseline Title / Period Description</label>
            <input
              type="text"
              value={form.periodLabel || ""}
              onChange={handleChange("periodLabel")}
              placeholder="e.g. FY 2025-2026 Opening Balance (Past 1 Year)"
              className="w-full bg-muted/40 border border-border px-3.5 py-2 rounded-lg text-sm outline-none focus:border-terracotta"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Initial Cash &amp; Bank Balance on Hand (₹)</label>
            <input
              type="number"
              value={form.openingCashOnHand ?? ""}
              onChange={handleChange("openingCashOnHand")}
              placeholder="0"
              className="w-full bg-muted/40 border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
            />
          </div>
        </div>

        {/* Sales & Orders Baseline */}
        <div className="border border-border/80 rounded-xl p-4 bg-muted/20 space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-terracotta">Sales, Orders &amp; Logistics Baseline</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Initial Sales Revenue (₹)</label>
              <input
                type="number"
                value={form.openingRevenue ?? ""}
                onChange={handleChange("openingRevenue")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Completed Orders Count</label>
              <input
                type="number"
                value={form.openingOrdersCount ?? ""}
                onChange={handleChange("openingOrdersCount")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Total Units / Quantity Sold</label>
              <input
                type="number"
                value={form.openingQuantitySold ?? ""}
                onChange={handleChange("openingQuantitySold")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Courier &amp; Shipping Outflow (₹)</label>
              <input
                type="number"
                value={form.openingShipping ?? ""}
                onChange={handleChange("openingShipping")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
              />
            </div>
          </div>
        </div>

        {/* Manufacturing, Losses & Profit Baseline */}
        <div className="border border-border/80 rounded-xl p-4 bg-muted/20 space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-terracotta">Costs, Expenses, Losses &amp; Net Profit Baseline</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Raw Materials &amp; COGS (₹)</label>
              <input
                type="number"
                value={form.openingCOGS ?? ""}
                onChange={handleChange("openingCOGS")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Operational Expenses (₹)</label>
              <input
                type="number"
                value={form.openingExpenses ?? ""}
                onChange={handleChange("openingExpenses")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Initial Losses &amp; Damage (₹)</label>
              <input
                type="number"
                value={form.openingLosses ?? ""}
                onChange={handleChange("openingLosses")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta text-rose-700 dark:text-rose-400 font-bold"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Initial Business Net Profit (₹)</label>
              <input
                type="number"
                value={form.openingProfit ?? ""}
                onChange={handleChange("openingProfit")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta text-emerald-700 dark:text-emerald-400 font-bold"
              />
            </div>
          </div>
        </div>

        {/* Capital & Partner Draws Baseline */}
        <div className="border border-border/80 rounded-xl p-4 bg-muted/20 space-y-3">
          <h4 className="text-xs font-bold uppercase tracking-wider text-terracotta">Past 1-Year Partner Capital &amp; Draws</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-xs font-medium text-muted-foreground">Initial Total Capital Invested (₹)</label>
              <input
                type="number"
                value={form.openingCapital ?? ""}
                onChange={handleChange("openingCapital")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta font-semibold"
              />
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div>
                  <span className="text-[11px] text-muted-foreground block mb-0.5">Shuvasis Capital ₹</span>
                  <input
                    type="number"
                    value={form.openingCapitalShuvasis ?? ""}
                    onChange={handleChange("openingCapitalShuvasis")}
                    placeholder="0"
                    className="w-full bg-background border border-border px-2.5 py-1.5 rounded text-xs font-mono"
                  />
                </div>
                <div>
                  <span className="text-[11px] text-muted-foreground block mb-0.5">Sourav Capital ₹</span>
                  <input
                    type="number"
                    value={form.openingCapitalSourav ?? ""}
                    onChange={handleChange("openingCapitalSourav")}
                    placeholder="0"
                    className="w-full bg-background border border-border px-2.5 py-1.5 rounded text-xs font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-medium text-muted-foreground">Initial Partner Withdrawals / Draws (₹)</label>
              <input
                type="number"
                value={form.openingWithdrawals ?? ""}
                onChange={handleChange("openingWithdrawals")}
                placeholder="0"
                className="w-full bg-background border border-border px-3.5 py-2 rounded-lg text-sm font-mono outline-none focus:border-terracotta font-semibold"
              />
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div>
                  <span className="text-[11px] text-muted-foreground block mb-0.5">Shuvasis Draws ₹</span>
                  <input
                    type="number"
                    value={form.openingWithdrawalsShuvasis ?? ""}
                    onChange={handleChange("openingWithdrawalsShuvasis")}
                    placeholder="0"
                    className="w-full bg-background border border-border px-2.5 py-1.5 rounded text-xs font-mono"
                  />
                </div>
                <div>
                  <span className="text-[11px] text-muted-foreground block mb-0.5">Sourav Draws ₹</span>
                  <input
                    type="number"
                    value={form.openingWithdrawalsSourav ?? ""}
                    onChange={handleChange("openingWithdrawalsSourav")}
                    placeholder="0"
                    className="w-full bg-background border border-border px-2.5 py-1.5 rounded text-xs font-mono"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Operational Notes / Remarks</label>
          <textarea
            rows={2}
            value={form.notes || ""}
            onChange={handleChange("notes")}
            placeholder="Additional notes regarding historical setup..."
            className="w-full bg-muted/40 border border-border px-3.5 py-2 rounded-lg text-sm outline-none focus:border-terracotta"
          />
        </div>

        <div className="pt-2 flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 rounded-full bg-terracotta text-primary-foreground font-medium text-xs hover:opacity-90 disabled:opacity-50 transition shadow-xs flex items-center gap-2"
          >
            <History className="w-4 h-4" />
            {busy ? "Saving Baseline…" : "Save Opening Balances Baseline"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// LOSSES & DAMAGE SUBPANEL
// ============================================================================
function LossesSubPanel({ losses = [], openingLosses = 0, refresh }) {
  const [form, setForm] = useState({ title: "", category: "Damage", amount: "", orderId: "", notes: "", date: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid loss amount");
    setBusy(true);
    try {
      await saveLoss(form);
      toast.success("Loss entry recorded");
      setForm({ title: "", category: "Damage", amount: "", orderId: "", notes: "", date: new Date().toISOString().slice(0, 10) });
      refresh();
    } catch (err) {
      toast.error(err.message || "Failed to record loss");
    } finally {
      setBusy(false);
    }
  };

  const totalRecordedLosses = losses.reduce((s, l) => s + Number(l.amount || 0), 0);
  const grandTotalLosses = Number(openingLosses || 0) + totalRecordedLosses;

  return (
    <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h3 className="font-serif text-xl font-bold flex items-center gap-2 text-rose-700 dark:text-rose-400">
            <ShieldAlert className="w-5 h-5" /> Business Loss &amp; Damage Tracker
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Track damaged stock, courier returns, lost consignments, and customer refund losses.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Total Loss Recorded</div>
          <div className="text-2xl font-serif font-bold text-rose-700 dark:text-rose-400 font-mono">
            ₹{grandTotalLosses.toLocaleString()}
          </div>
          <span className="text-[11px] text-muted-foreground">Baseline ₹{openingLosses} + Live ₹{totalRecordedLosses}</span>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-muted/30 border border-border/80 p-4 rounded-xl space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Log New Loss / Damage</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Title / Description</label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Damaged Leather Bag during Transit"
              className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs"
            >
              <option value="Damage">Transit Damage / Defect</option>
              <option value="Return">Courier Return Loss</option>
              <option value="Lost">Lost Consignment</option>
              <option value="Discount">Refund / Extra Discount Loss</option>
              <option value="Other">Other Loss</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Loss Amount (₹)</label>
            <input
              type="number"
              required
              min="1"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="e.g. 450"
              className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Associated Order ID (Optional)</label>
            <input
              type="text"
              value={form.orderId}
              onChange={(e) => setForm({ ...form, orderId: e.target.value })}
              placeholder="e.g. ORD-102"
              className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Notes / Action Taken</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Claim filed with Speedpost"
              className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-xs"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 bg-rose-700 text-white rounded-lg text-xs font-medium hover:bg-rose-800 disabled:opacity-50 transition"
          >
            {busy ? "Saving..." : "Record Loss Entry"}
          </button>
        </div>
      </form>

      {/* Log list */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Recorded Loss History</h4>
        {losses.length === 0 ? (
          <div className="text-center py-6 text-xs text-muted-foreground border border-dashed border-border rounded-xl">
            No live losses logged yet. Historical baseline loss of ₹{openingLosses} is included in summary.
          </div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-xl">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 border-b border-border text-left">
                <tr>
                  <th className="p-2.5">Date</th>
                  <th className="p-2.5">Title</th>
                  <th className="p-2.5">Category</th>
                  <th className="p-2.5">Order ID</th>
                  <th className="p-2.5">Amount</th>
                  <th className="p-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {losses.map((l) => (
                  <tr key={l.id} className="border-b border-border/50">
                    <td className="p-2.5 font-mono">{l.date}</td>
                    <td className="p-2.5 font-medium">{l.title}</td>
                    <td className="p-2.5"><span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 text-[10px]">{l.category}</span></td>
                    <td className="p-2.5 font-mono text-muted-foreground">{l.orderId || "—"}</td>
                    <td className="p-2.5 font-mono font-bold text-rose-700">₹{Number(l.amount).toLocaleString()}</td>
                    <td className="p-2.5">
                      <button
                        onClick={async () => {
                          if (confirm("Delete this loss entry?")) {
                            await deleteLoss(l.id);
                            toast.success("Loss entry deleted");
                            refresh();
                          }
                        }}
                        className="text-muted-foreground hover:text-rose-600 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
